/**
 * <bundle-buy-box> — bundle proposal selector + quantity multiplier (Frøya).
 * Intercepts the main product form submit and adds bundle lines via cart/add.js.
 */

if (!customElements.get('bundle-buy-box')) {
  function nbbbCartAddJsUrl() {
    const u = window.themeVariables?.routes?.cartAddUrl || window.routes?.cart_add_url || '/cart/add';
    return /\.js(\?|$)/.test(String(u)) ? u : `${String(u).replace(/\/?$/, '')}.js`;
  }

  function nbbbCartJsUrl() {
    const u = window.themeVariables?.routes?.cartUrl || window.routes?.cart_url || '/cart';
    return /\.js(\?|$)/.test(String(u)) ? u : `${String(u).replace(/\/?$/, '')}.js`;
  }

  /* Matches snippets/price.liquid — symbol + amount.
     Deliberately does NOT convert: `cents` always arrives in the presentment
     currency already (it comes from the data-nbbb-* attributes, which Liquid
     renders from live variant prices). It used to multiply by
     themeVariables.settings.conversionRate, which reads cart.currency
     .conversion_rate — not a real Liquid property, so it was always 1. Wiring a
     real rate in here would double-convert, and no rate can reproduce these
     prices anyway: some variants carry fixed per-market prices rather than
     converted ones (mature-hair-scalp is £71.00, not 89 × 0.7521 = £78.25). */
  function nbbbFormatMoney(cents) {
    const rounded = Math.round(Number(cents) / 100) * 100;

    let priceAmount = rounded / 100;
    const floored = Math.floor(priceAmount);
    if (priceAmount === floored) {
      priceAmount = floored;
    }

    const currencySymbol = window.themeVariables?.settings?.currencySymbol || '$';
    const currencyIso = window.themeVariables?.settings?.currencyCode || 'USD';

    if (currencySymbol === currencyIso) {
      return `${priceAmount} ${currencyIso}`;
    }
    return `${currencySymbol}${priceAmount}`;
  }

  function nbbbPercentOff(compareCents, saleCents) {
    const c = Number(compareCents) || 0;
    const s = Number(saleCents) || 0;
    if (c <= s || c <= 0) return 0;
    return Math.floor(((c - s) / c) * 100);
  }

  function nbbbParseMoneyTextToCents(text) {
    const digits = String(text || '').replace(/[^\d]/g, '');
    if (!digits) return 0;
    const major = Number(digits);
    return Number.isFinite(major) ? major * 100 : 0;
  }

  const FROYA_ATTR_TEMPLATE = '_froya_template';
  const FROYA_ATTR_BUNDLE_ID = '_froya_bundle_id';
  const FROYA_ATTR_SUB_BUNDLE_ID = '_froya_sub_bundle_id';
  const FROYA_DEFAULT_TRIGGER_SELECTOR =
    '.js-subscribtion__add-to-cart, .c-product-form__add-to-cart';
  const FROYA_DEFAULT_PROMO_CLASS = 'froya-bundle-promo';

  function froyaNormalizeSelector(raw) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return '';
    if (/^[.#\[]/.test(trimmed) || /[\s>,+]/.test(trimmed)) return trimmed;
    return `.${trimmed}`;
  }

  function froyaResolvePromoButton(scope, selector) {
    if (!scope || !selector) return null;
    return selector
      .split(',')
      .map((part) => froyaNormalizeSelector(part.trim()))
      .filter(Boolean)
      .map((part) => {
        try {
          return scope.querySelector(part);
        } catch (_) {
          return null;
        }
      })
      .find(Boolean) || null;
  }

  function froyaButtonMatchesSelector(btn, selector, scope) {
    if (!btn || !selector) return false;
    if (scope) {
      const resolved = froyaResolvePromoButton(scope, selector);
      if (resolved) return resolved === btn;
    }
    return selector
      .split(',')
      .map((part) => froyaNormalizeSelector(part.trim()))
      .filter(Boolean)
      .some((part) => {
        try {
          return btn.matches(part);
        } catch (_) {
          return false;
        }
      });
  }

  function froyaGidToNumericId(gid) {
    if (!gid) return null;
    const match = String(gid).match(/ProductVariant\/(\d+)/);
    if (match) return Number(match[1]);
    const numeric = Number(String(gid).trim());
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  }

  function froyaParseBundleConfig(root) {
    const script = root?.querySelector('script[data-froya-bundle-config]');
    if (!script?.textContent?.trim()) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      return froyaFilterBundles(parsed?.bundles);
    } catch (_) {
      return [];
    }
  }

  function froyaParseSubscriptionBundleConfig(root) {
    const script = root?.querySelector('script[data-froya-bundle-config]');
    if (!script?.textContent?.trim()) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      return froyaFilterSubscriptionBundles(parsed?.bundles);
    } catch (_) {
      return [];
    }
  }

  /** OTP product bundles only — never qty tiers or SUB bundles. */
  function froyaFilterBundles(bundles) {
    const list = Array.isArray(bundles) ? bundles : [];
    return list.filter((bundle) => {
      if (!bundle || bundle.active === false) return false;
      if (bundle.promoType === 'quantity_tier') return false;
      if (bundle.promoType === 'subscription_bundle') return false;
      if (!Array.isArray(bundle.items) || !bundle.items.length) return false;
      if (!bundle.discount || Number(bundle.discount.value) <= 0) return false;
      return true;
    });
  }

  function froyaFilterSubscriptionBundles(bundles) {
    const list = Array.isArray(bundles) ? bundles : [];
    return list.filter((bundle) => {
      if (!bundle || bundle.active === false) return false;
      if (bundle.promoType !== 'subscription_bundle') return false;
      if (!Array.isArray(bundle.items) || !bundle.items.length) return false;
      if (!bundle.discount || Number(bundle.discount.value) <= 0) return false;
      return true;
    });
  }

  const FROYA_PROXY_CONFIG_URL = '/apps/froya-bundles/config';

  function froyaBundleMatchesTemplate(bundle, templateKey) {
    const required = (bundle?.template || '').trim();
    if (!required) return true;
    return required === (templateKey || '').trim();
  }

  function froyaVariantMapFromItemsRaw(itemsRaw) {
    const map = new Map();
    String(itemsRaw || '')
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        const bits = part.split('~');
        const id = Number(bits[0]);
        const quantity = Number(bits[1] || 1);
        if (!Number.isFinite(id) || id <= 0) return;
        map.set(id, (map.get(id) || 0) + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1));
      });
    return map;
  }

  function froyaBundleDiscountEligibleItems(items, bundle) {
    const list = items || [];
    const included = list.filter((item) => item.applyFroyaBundleDiscount === true);
    if (included.length) return included;
    if (list.some((item) => item.applyFroyaBundleDiscount === false)) return [];
    return list;
  }

  function froyaVariantEligibleForBundleDiscount(bundle, variantIdNumeric) {
    if (!bundle?.items?.length || !variantIdNumeric) return false;
    const eligible = froyaBundleDiscountEligibleItems(bundle.items, bundle);
    return eligible.some((item) => froyaGidToNumericId(item.variantId) === variantIdNumeric);
  }

  /** Tag line for checkout: OTP = all bundle lines; subscription = non-selling-plan lines only when bundle allows. */
  function froyaShouldTagBundleLine(bundle, variantIdNumeric, isSubscription, hasSellingPlan) {
    if (!bundle?.id || !variantIdNumeric) return false;
    if (!isSubscription) return true;
    if (!bundle.applyDiscountToNonSubscriptionItems) return false;

    const inBundle = (bundle.items || []).some(
      (row) => froyaGidToNumericId(row.variantId) === variantIdNumeric,
    );
    return inBundle && !hasSellingPlan;
  }

  function froyaBundleMatchesVariantMap(bundle, variantMap) {
    for (const item of bundle.items || []) {
      const numericId = froyaGidToNumericId(item.variantId);
      const requiredQty = Number(item.quantity) || 1;
      if (!numericId || (variantMap.get(numericId) || 0) < requiredQty) {
        return false;
      }
    }
    return true;
  }

  function froyaSortBundles(bundles) {
    return [...bundles].sort((left, right) => {
      const leftQty = (left.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      const rightQty = (right.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      if (rightQty !== leftQty) return rightQty - leftQty;
      return (right.items || []).length - (left.items || []).length;
    });
  }

  /**
   * @param {object[]} bundles
   * @param {string} templateKey
   * @param {Map<number, number>} variantMap
   * @param {{ subscriptionCheckout?: boolean }} [options]
   */
  function froyaFindMatchingBundle(bundles, templateKey, variantMap, options = {}) {
    const sorted = froyaSortBundles(bundles);
    const matching = sorted.filter(
      bundle =>
        froyaBundleMatchesTemplate(bundle, templateKey) &&
        froyaBundleMatchesVariantMap(bundle, variantMap),
    );

    if (!matching.length) return null;

    if (options.subscriptionCheckout) {
      const subscriptionPromo = matching.find(
        bundle => bundle.applyDiscountToNonSubscriptionItems === true,
      );
      if (subscriptionPromo) return subscriptionPromo;
    }

    return matching[0];
  }

  function froyaNormalizeDiscount(discount, subtotalMajor) {
    if (!discount) return null;
    const type = discount.type === 'fixed_amount' ? 'fixed_amount' : 'percentage';
    const value = Number(discount.value);
    if (!Number.isFinite(value) || value <= 0) return null;
    return { type, value };
  }

  /* Shop currency -> presentment rate, for discount CONSTANTS only.
     Never apply this to a price: prices already arrive in the presentment
     currency (see nbbbFormatMoney). */
  function froyaPresentmentRate() {
    const rate = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  /** Mirrors extensions/discount-function-js discount_rounding.js (whole-currency final total). */
  function froyaDiscountedSubtotalMajor(subtotalMajor, discount) {
    if (subtotalMajor <= 0) return 0;

    if (discount.type === 'fixed_amount') {
      /* fixed_amount values are stored in the SHOP's currency and the checkout
         Function converts them to presentment before applying, so subtracting
         them raw from a presentment subtotal under-quotes the customer on every
         non-USD market. Measured on /en-eu/pages/bundle-1: sub base €164.10,
         164.10 - 30 showed €134 while checkout charged €138
         (164.10 - 30 x 0.879259 = 137.72). Confirmed again in GBP: the form
         showed £113 / £129 where 142.94 - 30 x 0.752138 = £120 is charged. */
      const off = (Number(discount.value) || 0) * froyaPresentmentRate();
      return Math.max(0, subtotalMajor - off);
    }

    /* Percentages are unitless — converting them would be wrong. */
    const pct = Number(discount.value) || 0;
    if (pct <= 0) return subtotalMajor;
    return Math.max(0, subtotalMajor * (1 - pct / 100));
  }

  function froyaApplyOtpDiscount(cents, discount) {
    const base = Number(cents) || 0;
    if (base <= 0 || !discount) return base;

    const subtotalMajor = base / 100;
    const roundedFinalMajor = Math.max(0, Math.round(froyaDiscountedSubtotalMajor(subtotalMajor, discount)));
    return roundedFinalMajor * 100;
  }

  function nbbbDebugEnabled(el) {
    return (
      el?.dataset?.debug === 'true' ||
      window.nbbbDebug === true ||
      /(?:\?|&)nbbb_debug=1(?:&|$)/.test(window.location.search)
    );
  }

  customElements.define(
    'bundle-buy-box',
    class BundleBuyBox extends HTMLElement {
      connectedCallback() {
        this._onDocSubmit = this._onDocSubmit.bind(this);
        this._onSubmitButtonClick = this._onSubmitButtonClick.bind(this);
        this._onContextClick = this._onContextClick.bind(this);
        this._onPageShow = this._onPageShow.bind(this);
        this._isSubmittingBundle = false;
        window.addEventListener('pageshow', this._onPageShow);
        // s-main-product covers lp-main-product / page-product (the Landing PDP
        // sections). Ordered so pages that already have s-featured-product
        // short-circuit first and keep their existing scope unchanged.
        this._contextRoot =
          this.closest('s-featured-product') ||
          this.closest('s-main-product') ||
          this.closest('.product') ||
          null;
        document.addEventListener('submit', this._onDocSubmit, true);
        if (this._contextRoot) {
          this._contextRoot.addEventListener('click', this._onContextClick, true);
        }
        this._submitBtn = this._resolveSubmitButton();
        if (this._submitBtn) {
          this._submitBtn.addEventListener('click', this._onSubmitButtonClick, true);
        }
        const productRoot = this._contextRoot;
        this._mediaWrapper =
          productRoot?.querySelector('.s-featured-product__gallery-wrapper') ||
          productRoot?.querySelector('.product__media-wrapper') ||
          productRoot?.querySelector('product-media.product__media') ||
          productRoot?.querySelector('.product__media') ||
          null;
        this._ssrGalleryWrapper  = this._mediaWrapper?.querySelector('[data-nbbb-ssr-gallery]') || null;
        this._nativeHiddenWrapper = this._ssrGalleryWrapper
          ? this._mediaWrapper?.querySelector('.nbbb__gallery-hidden') || null
          : null;
        this._originalCustomGallery = (this._nativeHiddenWrapper || this._mediaWrapper)
          ?.querySelector('.custom-gallery') || null;
        this._mediaGallery = this._nativeHiddenWrapper
          ? this._nativeHiddenWrapper.querySelector('[id^="MediaGallery-"]') || null
          : (document.getElementById(`MediaGallery-${this.sectionId}`) || null);
        this._dynamicCustomGallery = null;

        this._descEl =
          this.closest('[id^="ProductInfo-"]')?.querySelector('.s-featured-product__product-description') ||
          this.closest('[id^="ProductInfo-"]')?.querySelector('.product__description.rte') ||
          this.closest('.product__info')?.querySelector('.product-form__description.rte') ||
          null;
        this._originalDesc = this._descEl ? this._descEl.innerHTML : null;
        this._subheadlineEl =
          this.closest('[id^="ProductInfo-"]')?.querySelector('[data-nbbb-subheadline]') || null;
        this._subheadlineFallback = this._subheadlineEl?.dataset.nbbbSubheadlineFallback?.trim() || '';
        this._benefitsEl =
          this.closest('[id^="ProductInfo-"]')?.querySelector('[data-nbbb-benefits]') || null;
        this._subscriptionBenefitsEl =
          this.closest('[id^="ProductInfo-"]')?.querySelector('[data-nbbb-subscription-benefits]') ||
          null;
        this._subscriptionBenefitsGroupEl =
          this._subscriptionBenefitsEl?.closest('.skin-offer-purchase-card__benefits-group') || null;
        if (
          this.dataset.hideSubscriptionBenefitsOneTime === 'true' &&
          this._subscriptionBenefitsGroupEl
        ) {
          this._subscriptionBenefitsGroupEl.classList.add('nbbb--benefits-group-animatable');
        }
        this._subscriptionRefillNoteEl =
          this.closest('[id^="ProductInfo-"]')?.querySelector('[data-nbbb-subscription-refill-note]') ||
          null;
        this._infoEl = this.querySelector('[data-nbbb-info-block]') || null;
        this._qtyRoot = this.querySelector('[data-nbbb-qty-root]') || this.querySelector('.nbb__bundle-qty');
        if (nbbbDebugEnabled(this)) this.dataset.debug = 'true';
        this._froyaBundles = froyaParseBundleConfig(this);
        this._froyaSubBundles = froyaParseSubscriptionBundleConfig(this);
        this._froyaConfigProxyPromise = null;
        this._onPurchaseTypeChange = this._onPurchaseTypeChange.bind(this);
        this._onQtyRootClick = this._onQtyRootClick.bind(this);
        this._onQtyRootChange = this._onQtyRootChange.bind(this);
        this._onCountryChange = this._onCountryChange.bind(this);
        this._hideQtySelectors = this.dataset.hideQtySelectors === 'true';
        if (this._hideQtySelectors) {
          this.classList.add('nbbb--hide-qty-selectors');
        }

        this._bindProposalCards();
        if (!this._hideQtySelectors) {
          this._bindQtyOptions();
          this._applyDefaultQty();
        }
        this._bindPurchaseType();
        this._hideThemeQuantitySelectors();
        this._applyDefaultProposal();
        this._syncFroyaProposalCards();
        this._initSkinOffer();
        this._refreshAllPrices();
        this._syncPrimaryVariantHint();
        this._syncSkinOfferPurchasePrices();
        this._patchSubmitButtonLabel();
        this._scheduleSkinOfferButtonPatch();
        this._syncGalleryForSelectedProposal();
        this._syncDescriptionForSelectedProposal();
        this._syncInfoForSelectedProposal();
        this._syncDeliveryFrequencyVisibility();
        this._updateRefillInfo();
        this.#syncSubscriptionBenefits();
        this.#syncSubscriptionBenefitsGroupVisibility();
        this._syncFreeGiftsPanel();
        this._syncSubmitButtonGiftTrigger();
        this._syncFroyaPromoButton();
        this._bindCountryChange();
        this._bootstrapFroyaConfig();
        this._syncSingleProposalUi();
        this._logFroyaDiagnostics();
      }

      _bootstrapFroyaConfig() {
        if (this._froyaBundles?.length || this._froyaSubBundles?.length) return;

        this._loadFroyaConfigFromProxy().then((loaded) => {
          if (!loaded) return;
          this._syncFroyaProposalCards();
          this._refreshAllPrices();
          this._syncSkinOfferPurchasePrices();
          this._patchSubmitButtonLabel();
          this._syncFroyaPromoButton();
          this._logFroyaDiagnostics();
        });
      }

      _loadFroyaConfigFromProxy() {
        if (this._froyaConfigProxyPromise) return this._froyaConfigProxyPromise;

        this._froyaConfigProxyPromise = fetch(FROYA_PROXY_CONFIG_URL, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
          .then((response) => {
            if (!response.ok) return false;
            return response.json();
          })
          .then((payload) => {
            const bundles = froyaFilterBundles(payload?.bundles);
            const subBundles = froyaFilterSubscriptionBundles(payload?.bundles);
            if (!bundles.length && !subBundles.length) return false;
            this._froyaBundles = bundles;
            this._froyaSubBundles = subBundles;
            this.dataset.froyaConfigPresent = 'true';
            this.dataset.froyaConfigSource = 'app-proxy';
            this.#log('loaded bundle config via app proxy', {
              otp: bundles.length,
              sub: subBundles.length,
            });
            return true;
          })
          .catch(() => false);

        return this._froyaConfigProxyPromise;
      }

      _logFroyaDiagnostics() {
        if (!nbbbDebugEnabled(this)) return;

        const configScript = this.querySelector('script[data-froya-bundle-config]');
        const card = this._getSelectedProposalCard();
        const purchaseType = this._isSubscriptionPurchase() ? 'subscription' : 'one_time';
        const bundle = card ? this._getFroyaBundleForCard(card) : null;
        const btn = this._resolveSubmitButton();

        console.group('[bundle-buy-box] froya diagnostics');
        console.log('shop metafield in liquid', this.dataset.froyaConfigPresent);
        console.log('config source', this.dataset.froyaConfigSource || 'liquid');
        console.log('config script in DOM', Boolean(configScript));
        console.log('parsed bundles', this._ensureFroyaBundles().length);
        console.log('template', this._getFroyaTemplate());
        console.log('selected proposal', card?.dataset?.nbbbProposalSlot ?? null);
        console.log('purchase type', purchaseType);
        console.log('matched bundle', bundle?.id ?? null, bundle?.title ?? null);
        if (card && purchaseType === 'one_time') {
          const idx = this.selectedQtyIndex;
          const otBase = this._otBaseCentsForQtyIdx(card, idx);
          const otSale = this._otCentsForQtyIdx(card, idx);
          console.log('otp item total (cents)', otBase, '→ after promo (cents)', otSale);
          if (bundle?.discount) {
            console.log('promo discount', bundle.discount.type, bundle.discount.value);
          }
        }
        console.log('submit button', btn?.className ?? null);
        console.log('promo class on button', btn?.classList.contains(this._getFroyaPromoClass(bundle)) ?? false);

        if (this.dataset.froyaConfigPresent !== 'true') {
          console.warn(
            'Missing theme bundle config in Liquid. Publish theme with latest bundle-buy-box.liquid (shop.metaobjects.froya_theme_config). App proxy may still load config asynchronously.',
          );
        } else if (!configScript) {
          console.warn(
            'Liquid saw bundle_config but script tag is missing — publish theme with latest bundle-buy-box.liquid.',
          );
        } else if (!this._ensureFroyaBundles().length) {
          console.warn('Config script present but no active bundles parsed from JSON.');
        } else if (
          purchaseType === 'subscription' &&
          bundle?.applyDiscountToNonSubscriptionItems
        ) {
          const idx = this.selectedQtyIndex;
          const lines = this._parseItems(card);
          console.log(
            'Subscription + add-on promo: subscription lines are not tagged; OTP add-on lines get _froya_bundle_id at checkout.',
          );
          lines.forEach(line => {
            console.log('line tag plan', {
              variantId: line.id,
              sellingPlanId: line.sellingPlanId || null,
              willTagFroyaBundleId: froyaShouldTagBundleLine(
                bundle,
                line.id,
                true,
                Boolean(line.sellingPlanId),
              ),
            });
          });
          console.log(
            'subscription card cents',
            this._subscriptionSkinOfferTotals(card, idx),
          );
        } else if (purchaseType !== 'one_time') {
          console.warn('Select One Time — promo class and checkout tag are OTP-only.');
        } else if (!bundle) {
          console.warn('No bundle matches current proposal variants + template.');
        }
        console.groupEnd();
      }

      disconnectedCallback() {
        this.#removeInjectedCustomGalleries();
        this.#setGalleryWrapperMode('native');
        this._showNativeGalleries();
        document.removeEventListener('submit', this._onDocSubmit, true);
        window.removeEventListener('pageshow', this._onPageShow);
        this._qtyAbort?.abort();
        this._qtyAbort = null;
        if (this._contextRoot) {
          this._contextRoot.removeEventListener('click', this._onContextClick, true);
          this._contextRoot.removeEventListener('change', this._onPurchaseTypeChange, true);
        }
        if (this._submitBtn) {
          this._submitBtn.removeEventListener('click', this._onSubmitButtonClick, true);
        }
        clearTimeout(this._skinOfferPatchTimer);
        this._skinOfferPatchTimer = null;
        const countrySelect = document.querySelector('#country_code');
        if (countrySelect) {
          countrySelect.removeEventListener('change', this._onCountryChange);
        }
      }

      _bindCountryChange() {
        const countrySelect = document.querySelector('#country_code');
        if (!countrySelect || countrySelect.dataset.nbbbCountryBound === 'true') return;
        countrySelect.dataset.nbbbCountryBound = 'true';
        countrySelect.addEventListener('change', this._onCountryChange);
      }

      _onCountryChange() {
        this._refreshAllPrices();
        this._syncSkinOfferPurchasePrices();
        this._patchSubmitButtonLabel();
        this._updateRefillInfo();
        this.#syncSubscriptionBenefits();
        this.#syncSubscriptionBenefitsGroupVisibility();
        this._syncFreeGiftsPanel();
      }

      #log(...args) {
        if (!nbbbDebugEnabled(this)) return;
        console.log('[bundle-buy-box]', ...args);
      }

      _getEffectivePromoSelector(bundle) {
        const fromBundle = bundle?.triggerSelector?.trim();
        if (fromBundle) return fromBundle;

        const fromTheme = this.dataset.froyaPromoTriggerSelector?.trim();
        if (fromTheme) return fromTheme;

        return '';
      }

      _getFroyaTriggerSelector(bundle) {
        const effective = this._getEffectivePromoSelector(bundle);
        if (effective) return froyaNormalizeSelector(effective);

        return FROYA_DEFAULT_TRIGGER_SELECTOR;
      }

      _getFroyaPromoClass(bundle) {
        const fromBundle = bundle?.promoClass?.trim();
        if (fromBundle) return fromBundle.replace(/^\./, '');

        const fromTheme = this.dataset.froyaPromoButtonClass?.trim();
        if (fromTheme) return fromTheme.replace(/^\./, '');

        return FROYA_DEFAULT_PROMO_CLASS;
      }

      _ensureFroyaBundles() {
        if (!this._froyaBundles?.length) {
          this._froyaBundles = froyaParseBundleConfig(this);
        }
        if (!this._froyaBundles?.length) {
          void this._loadFroyaConfigFromProxy();
        }
        return this._froyaBundles;
      }

      _ensureFroyaSubBundles() {
        if (!this._froyaSubBundles?.length) {
          this._froyaSubBundles = froyaParseSubscriptionBundleConfig(this);
        }
        if (!this._froyaSubBundles?.length) {
          void this._loadFroyaConfigFromProxy();
        }
        return this._froyaSubBundles || [];
      }

      _getFroyaSubBundleForCard(card, idx) {
        if (!card || !this._ensureFroyaSubBundles().length) return null;
        const bundle = froyaFindMatchingBundle(
          this._froyaSubBundles,
          this._getFroyaTemplate(),
          this._variantMapForCard(card, idx),
          { subscriptionCheckout: false },
        );
        if (nbbbDebugEnabled(this) && bundle) {
          this.#log('froya SUB bundle match', {
            bundleId: bundle.id,
            title: bundle.title,
          });
        }
        return bundle;
      }

      _getFroyaTemplate() {
        const override = this.dataset.froyaTemplateOverride?.trim();
        if (override) return override;

        const view = new URLSearchParams(window.location.search).get('view');
        if (view?.trim()) {
          return `page.${view.trim()}`;
        }

        const fromLiquid = this.dataset.froyaTemplate?.trim();
        if (fromLiquid) return fromLiquid;

        return '';
      }

      _variantMapForCard(card, idx) {
        const raw = this._itemsRawForQtyIdx(card, idx ?? this.selectedQtyIndex);
        return froyaVariantMapFromItemsRaw(raw);
      }

      _getFroyaBundleForCard(card, idx, options = {}) {
        if (!card || !this._ensureFroyaBundles().length) return null;
        const subscriptionCheckout =
          options.subscriptionCheckout !== undefined
            ? options.subscriptionCheckout
            : this._isSubscriptionPurchase();
        const bundle = froyaFindMatchingBundle(
          this._froyaBundles,
          this._getFroyaTemplate(),
          this._variantMapForCard(card, idx),
          { subscriptionCheckout },
        );
        if (nbbbDebugEnabled(this) && bundle) {
          this.#log('froya bundle match', {
            bundleId: bundle.id,
            title: bundle.title,
            applyDiscountToNonSubscriptionItems: bundle.applyDiscountToNonSubscriptionItems === true,
            subscriptionCheckout,
          });
        }
        return bundle;
      }

      _syncFroyaProposalCards() {
        if (!this._ensureFroyaBundles().length) return;

        this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-idx]').forEach((card) => {
          const bundle = this._getFroyaBundleForCard(card, 0);
          if (bundle?.id) {
            card.dataset.froyaBundleId = bundle.id;
          } else {
            delete card.dataset.froyaBundleId;
          }
        });
      }

      _syncFroyaPromoButton() {
        const card = this._getSelectedProposalCard();
        const bundle = card ? this._getFroyaBundleForCard(card) : null;
        const btn = this._findSubmitButton();
        if (!btn) return;

        const isOtp = !this._isSubscriptionPurchase();
        const promoClass = this._getFroyaPromoClass(bundle);

        if (bundle && isOtp) {
          if (this._lastPromoClass && this._lastPromoClass !== promoClass) {
            btn.classList.remove(this._lastPromoClass);
          }
          btn.classList.add(promoClass);
          this._lastPromoClass = promoClass;
          btn.dataset.froyaBundleId = bundle.id;
          const templateKey = this._getFroyaTemplate();
          if (templateKey) btn.dataset.froyaTemplate = templateKey;
          const selector = this._getFroyaTriggerSelector(bundle);
          if (selector) btn.dataset.froyaPromoTrigger = selector;
          this.#log('froya promo active', {
            bundleId: bundle.id,
            template: templateKey,
            selector,
            promoClass,
          });
        } else {
          this._clearFroyaPromoButton(btn, bundle);
        }
      }

      _clearFroyaPromoButton(btn, bundle) {
        if (!btn) return;
        const promoClass = this._lastPromoClass || this._getFroyaPromoClass(bundle);
        btn.classList.remove(promoClass);
        this._lastPromoClass = null;
        delete btn.dataset.froyaBundleId;
        delete btn.dataset.froyaTemplate;
        delete btn.dataset.froyaPromoTrigger;
      }

      _shouldApplyFroyaPromo(submitBtn, card) {
        const bundle = card ? this._getFroyaBundleForCard(card) : null;
        if (!bundle || this._isSubscriptionPurchase()) return false;

        const selector = this._getEffectivePromoSelector(bundle);
        if (!selector) return true;
        if (!submitBtn) return true;

        return froyaButtonMatchesSelector(
          submitBtn,
          froyaNormalizeSelector(selector),
          this._contextRoot,
        );
      }

      _isSkinOffer() {
        return this.dataset.skinOffer === 'true' || this.classList.contains('nbbb--skin-offer');
      }

      _isSkinOfferContext() {
        if (this._isSkinOffer()) return true;
        if (this.dataset.skinOfferRefillText) return true;
        const root = this._contextRoot;
        return Boolean(root?.classList?.contains('s-featured-product--skin-offer'));
      }

      _skinOfferRefillText(card) {
        const fromProposal = card?.dataset?.nbbbRefillFootnoteText?.trim();
        if (fromProposal) return fromProposal;
        const custom =
          this.dataset.skinOfferRefillText?.trim() ||
          this.dataset.defaultRefillFootnoteText?.trim();
        return custom || 'refill ships every 40 days';
      }

      _usesAlternativeRefillLayout() {
        return this.dataset.alternativeLayoutRefillNotice === 'true';
      }

      #getProposalCustomRefillNoticeHtml(proposalIdx) {
        const tpl = this.querySelector(
          `template[data-nbbb-custom-refill-notice-template="${proposalIdx}"]`,
        );
        if (tpl?.innerHTML.trim()) return tpl.innerHTML.trim();

        const fallback = this.querySelector('template[data-nbbb-custom-refill-notice-template="fallback"]');
        return fallback?.innerHTML.trim() || '';
      }

      #buildCustomRefillNoticeHtml(card, idx) {
        if (!card) return '';
        const proposalIdx = card.dataset.nbbbProposalIdx;
        const raw = this.#getProposalCustomRefillNoticeHtml(proposalIdx);
        if (!raw) return '';
        return this.#applySubscriptionBenefitTokens(raw, card, idx);
      }

      #resolveAlternativeRefillNoticeHtml(card, idx) {
        if (!card) return '';

        const customHtml = this.#buildCustomRefillNoticeHtml(card, idx);
        if (customHtml) return customHtml;

        const proposalIdx = card.dataset.nbbbProposalIdx;
        const rawContent =
          this.#getProposalSubscriptionBenefitsHtml(proposalIdx) ||
          this.#buildSubscriptionBenefitsFallbackHtml();
        const listHtml = this.#stripSubscriptionRefillNote(rawContent);
        return this.#applySubscriptionBenefitTokens(listHtml, card, idx);
      }

      _shouldShowSubscriptionRefillFootnote() {
        return this.dataset.skinOfferShowRefillFootnote === 'true';
      }

      _initSkinOffer() {
        if (!this._isSkinOffer()) return;

        const subscriptionEl = this._contextRoot?.querySelector('c-subscription[data-skin-offer-layout]');
        const subscriptionForm = subscriptionEl?.querySelector('.js-subscribtion__subscription-form');
        if (subscriptionForm && !subscriptionForm.querySelector('#selling-plan-fieldset')) {
          const stubFieldset = document.createElement('fieldset');
          stubFieldset.id = 'selling-plan-fieldset';
          stubFieldset.hidden = true;
          stubFieldset.classList.add('is-hidden');
          subscriptionForm.appendChild(stubFieldset);
        }

        const sub = this._contextRoot?.querySelector('input[name="purchase_type"][value="subscription"]');
        const oneTime = this._contextRoot?.querySelector('input[name="purchase_type"][value="one_time"]');
        if (sub && oneTime && !sub.checked && !oneTime.checked) {
          sub.checked = true;
          sub.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const btn = this._findSubmitButton();
        const priceWrap = btn?.querySelector('.js-subscribtion__price-wrapper');
        if (priceWrap) priceWrap.hidden = true;

        this._syncSingleProposalUi();
        this._initSkinOfferPriceGuard();
        this._scheduleSkinOfferButtonPatch();
      }

      _syncSingleProposalUi() {
        const cards = this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-idx]');
        const single = cards.length === 1;
        this.classList.toggle('nbbb--single-proposal', single);
        if (single && cards[0] && !cards[0].classList.contains('is-selected')) {
          this._selectProposal(cards[0]);
        }
      }

      _initSkinOfferPriceGuard() {
        if (this._skinOfferPriceGuard) return;

        const root = this._contextRoot || document;
        const otCard = root.querySelector('.skin-offer-purchase-card--one-time');
        const main = otCard?.querySelector('.js-subscribtion__main-price .product_price');
        if (!otCard || !main) return;

        this._skinOfferPriceGuard = new MutationObserver(() => {
          const expected = Number(otCard.dataset.nbbbSyncedSaleCents || 0);
          if (expected <= 0) return;
          const shown = nbbbParseMoneyTextToCents(main.textContent);
          if (shown === 0) {
            this._syncSkinOfferPurchasePrices();
          }
        });
        this._skinOfferPriceGuard.observe(main, {
          characterData: true,
          childList: true,
          subtree: true,
        });
      }

      _scheduleSkinOfferButtonPatch() {
        if (!this._isSkinOffer()) return;
        clearTimeout(this._skinOfferPatchTimer);
        this._skinOfferPatchTimer = setTimeout(() => {
          this._ensureFroyaBundles();
          this._syncFroyaProposalCards();
          this._syncSkinOfferPurchasePrices();
          this._patchSubmitButtonLabel();
          this._updateRefillInfo();
          this.#syncSubscriptionBenefits();
          this.#syncSubscriptionBenefitsGroupVisibility();
          this._syncFreeGiftsPanel();
        }, 0);
      }

      _bindPurchaseType() {
        const root = this._contextRoot || document;
        root.addEventListener('change', this._onPurchaseTypeChange, true);

        const form = this._resolveProductForm();
        if (form && !form.dataset.nbbbPurchaseTypeBound) {
          form.dataset.nbbbPurchaseTypeBound = 'true';
          form.addEventListener('change', this._onPurchaseTypeChange);
        }
      }

      _onPurchaseTypeChange(event) {
        if (!event.target?.matches?.('input[name="purchase_type"]')) return;
        this._syncGalleryForSelectedProposal();
        this._syncDeliveryFrequencyVisibility();
        this._refreshAllPrices();
        this._syncSkinOfferPurchasePrices();
        this._patchSubmitButtonLabel();
        this._scheduleSkinOfferButtonPatch();
        this._updateRefillInfo();
        this.#syncSubscriptionBenefits();
        this.#syncSubscriptionBenefitsGroupVisibility();
        this._syncSubmitButtonGiftTrigger();
        requestAnimationFrame(() => this._syncFreeGiftsPanel());
      }

      _syncDeliveryFrequencyVisibility() {
        const root = this._contextRoot || document;
        const isSkinOffer =
          this._isSkinOffer() || root.classList.contains('s-featured-product--skin-offer');
        if (this.dataset.hideDeliveryFrequency !== 'true' && !isSkinOffer) return;

        root.classList.add('nbbb--hide-delivery-frequency');
        const fieldset = root.querySelector('#selling-plan-fieldset');
        if (!fieldset) return;
        fieldset.classList.add('is-hidden');
        fieldset.setAttribute('hidden', '');
        fieldset.setAttribute('aria-hidden', 'true');
        fieldset.style.setProperty('display', 'none', 'important');
      }

      _getPurchaseTypeRoot() {
        return this._contextRoot || this.closest('s-featured-product') || null;
      }

      _isSubscriptionPurchase() {
        const form = this._resolveProductForm();
        if (form) {
          const formChecked = form.querySelector('input[name="purchase_type"]:checked');
          if (formChecked) return formChecked.value === 'subscription';
        }

        const root = this._getPurchaseTypeRoot();
        if (!root) return false;

        const checked = root.querySelector('input[name="purchase_type"]:checked');
        if (checked) return checked.value === 'subscription';

        const oneTimeOption = root.querySelector('input[name="purchase_type"][value="one_time"]');
        if (oneTimeOption) return false;

        const hiddenSub =
          form?.querySelector('input[name="purchase_type"][value="subscription"]') ||
          root.querySelector('input[name="purchase_type"][value="subscription"]:not([type="radio"])');
        return Boolean(hiddenSub);
      }

      _shouldIncludeProposalFreeGifts() {
        if (!this._isFreeGiftsEligibleForCustomer()) return false;

        const purchaseType = this.dataset.freeGiftsPurchaseType || 'subscription';
        if (purchaseType === 'always' || this.dataset.freeGiftsSubscriptionOnly === 'false') {
          return true;
        }
        return this._isSubscriptionPurchase();
      }

      _isFreeGiftsEligibleForCustomer() {
        if (this.dataset.freeGiftsEligible === 'false') return false;
        if (this.dataset.freeGiftsFirstOrderOnly !== 'true') return true;

        try {
          if (window.localStorage?.getItem('nbbb_completed_order') === '1') return false;
        } catch (_) {
          /* ignore */
        }

        return true;
      }

      _parseFreeGiftVariantIds(card) {
        if (!card) return [];

        const raw = (card.dataset.nbbbFreeGiftIds || card.dataset.nbbbFreeGiftId || '').trim();
        if (!raw) return [];

        return raw
          .split(',')
          .map((id) => Number(String(id).trim()))
          .filter((id) => Number.isFinite(id) && id > 0);
      }

      _getFreeGiftVariantIds(card) {
        if (!this._shouldIncludeProposalFreeGifts()) return [];
        return this._parseFreeGiftVariantIds(card);
      }

      _syncSubmitButtonGiftTrigger() {
        const btn = this._findSubmitButton();
        if (!btn) return;

        if (!this._giftTriggerHandle) {
          this._giftTriggerHandle = btn.getAttribute('data-gift-trigger') || '';
        }

        /* This buy box puts the gift in its own add payload, so the engine must
           not also add one on click. The `_froya_no_gift` line property stops
           the reconciler; this stops the click fast path, which runs before any
           line exists and so cannot see that property. */
        if (this.dataset.froyaNoGift === 'true') {
          btn.removeAttribute('data-gift-trigger');
          return;
        }

        if (this._isSkinOffer() && this._shouldIncludeProposalFreeGifts()) {
          btn.removeAttribute('data-gift-trigger');
          return;
        }

        if (this._shouldIncludeProposalFreeGifts() && this._giftTriggerHandle) {
          btn.setAttribute('data-gift-trigger', this._giftTriggerHandle);
          return;
        }

        btn.removeAttribute('data-gift-trigger');
      }

      _getGalleryTemplate(proposalIdx) {
        const sub = this._isSubscriptionPurchase();
        if (sub) {
          const subTpl = this.querySelector(`template[data-nbbb-gallery-template-sub="${proposalIdx}"]`);
          if (subTpl) return subTpl;
        }
        return this.querySelector(`template[data-nbbb-gallery-template="${proposalIdx}"]`);
      }

      get sectionId() {
        const declared = this.dataset.sectionId || '';
        if (declared && document.getElementById(`product-form-${declared}`)) return declared;
        const infoSection = this.closest('[id^="ProductInfo-"]');
        if (infoSection) {
          const id = infoSection.id.replace('ProductInfo-', '');
          if (id && document.getElementById(`product-form-${id}`)) return id;
        }
        return declared;
      }

      get selectedQtyIndex() {
        if (this.dataset.hideQtySelectors === 'true') return 0;

        const checked = this.querySelector('.nbb__bundle-qty [data-nbbb-qty-radio]:checked');
        if (checked) {
          const fromRadio = checked.closest('.nbb__delivery-option');
          const v = parseInt(fromRadio?.dataset.nbbbQtyIdx ?? checked.value ?? '0', 10);
          if (Number.isFinite(v) && v >= 0) return v;
        }
        const opt = this.querySelector('.nbb__bundle-qty .nbb__delivery-option.is-selected');
        const v = parseInt(opt?.dataset.nbbbQtyIdx || '0', 10);
        return Number.isFinite(v) && v >= 0 ? v : 0;
      }

      get selectedQtyUnitCount() {
        if (this.dataset.hideQtySelectors === 'true') return 1;

        const checked = this.querySelector('.nbb__bundle-qty [data-nbbb-qty-radio]:checked');
        const opt =
          checked?.closest('.nbb__delivery-option') ||
          this.querySelector('.nbb__bundle-qty .nbb__delivery-option.is-selected');
        const v = parseInt(opt?.dataset.nbbbUnitCount || '1', 10);
        return Number.isFinite(v) && v >= 1 ? v : 1;
      }

      _itemsRawForQtyIdx(card, idx) {
        if (idx === 1) return card.dataset.nbbbItemsQ1 || card.dataset.nbbbItems || '';
        if (idx === 2) return card.dataset.nbbbItemsQ2 || card.dataset.nbbbItems || '';
        return card.dataset.nbbbItems || '';
      }

      _parseBundleLineParts(card, idx) {
        const raw = this._itemsRawForQtyIdx(card, idx);
        if (!raw || (!raw.includes('~') && !raw.includes('|'))) return [];

        return raw
          .split('|')
          .map(part => part.trim())
          .filter(Boolean)
          .map(part => {
            const bits = part.split('~');
            const id = Number(bits[0]);
            const quantity = Number(bits[1] || 1);
            let sellingPlanId = '';
            let lineCmpCents = 0;
            let lineSubCents = 0;
            let lineOtCents = 0;

            if (bits.length >= 4) {
              sellingPlanId = (bits[2] || '').trim();
              const cmp = parseInt(bits[3], 10);
              if (Number.isFinite(cmp) && cmp > 0) lineCmpCents = cmp;
            } else if (bits.length >= 3) {
              sellingPlanId = (bits[2] || '').trim();
            }

            let lineRecurringCents = 0;
            let hasRecurringField = false;

            if (bits.length >= 6) {
              const sub = parseInt(bits[4], 10);
              const ot = parseInt(bits[5], 10);
              if (Number.isFinite(sub) && sub >= 0) lineSubCents = sub;
              if (Number.isFinite(ot) && ot >= 0) lineOtCents = ot;
            }

            if (bits.length >= 7) {
              hasRecurringField = true;
              const recurring = parseInt(bits[6], 10);
              if (Number.isFinite(recurring) && recurring >= 0) lineRecurringCents = recurring;
            }

            if (!Number.isFinite(id) || id <= 0) return null;
            return {
              id,
              quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
              sellingPlanId,
              lineCmpCents,
              lineSubCents,
              lineOtCents,
              lineRecurringCents,
              hasRecurringField,
            };
          })
          .filter(Boolean);
      }

      _subscriptionSkinOfferTotals(card, idx) {
        const fallbackSub = this._subCentsForQtyIdx(card, idx);
        const fallbackCmp = this._cmpCentsForQtyIdx(card, idx);
        const lines = this._parseBundleLineParts(card, idx);
        const bundle = this._getFroyaBundleForCard(card, idx, {
          subscriptionCheckout: true,
        });
        const subBundle = this._getFroyaSubBundleForCard(card, idx);

        if (!lines.length) {
          return this._applySubBundlePreviewDiscount(
            { saleCents: fallbackSub, compareCents: fallbackCmp },
            subBundle,
            fallbackSub,
          );
        }

        const hasPerLinePricing = lines.some(
          line => line.lineOtCents > 0 || (line.sellingPlanId && line.lineSubCents > 0),
        );

        if (!hasPerLinePricing && bundle?.applyDiscountToNonSubscriptionItems && bundle.discount) {
          let addonOt = 0;
          for (const line of lines) {
            if (line.sellingPlanId) continue;
            addonOt += (line.lineCmpCents || 0) * line.quantity;
          }
          if (addonOt > 0) {
            const discount = froyaNormalizeDiscount(bundle.discount, addonOt / 100);
            if (discount) {
              const addonSale = froyaApplyOtpDiscount(addonOt, discount);
              const subscriptionPortion = Math.max(0, fallbackSub - addonOt);
              return this._applySubBundlePreviewDiscount(
                {
                  saleCents: subscriptionPortion + addonSale,
                  compareCents: fallbackCmp,
                },
                subBundle,
                subscriptionPortion,
              );
            }
          }
        }

        if (!hasPerLinePricing) {
          return this._applySubBundlePreviewDiscount(
            { saleCents: fallbackSub, compareCents: fallbackCmp },
            subBundle,
            fallbackSub,
          );
        }

        const bundleVariantIds = new Set(
          (bundle?.items || [])
            .map(item => froyaGidToNumericId(item.variantId))
            .filter(Boolean),
        );
        const restrictToBundleVariants =
          bundle?.applyDiscountToNonSubscriptionItems === true && bundleVariantIds.size > 0;

        let subscriptionSaleCents = 0;
        let otherSaleCents = 0;
        let compareCents = 0;

        for (const line of lines) {
          const qty = line.quantity;
          compareCents += (line.lineCmpCents || 0) * qty;

          if (line.sellingPlanId) {
            subscriptionSaleCents += (line.lineSubCents || 0) * qty;
            continue;
          }

          if (restrictToBundleVariants && !bundleVariantIds.has(line.id)) {
            otherSaleCents += (line.lineSubCents || line.lineOtCents || line.lineCmpCents || 0) * qty;
            continue;
          }

          const otBase =
            line.lineOtCents > 0
              ? line.lineOtCents
              : line.lineSubCents > 0
                ? line.lineSubCents
                : line.lineCmpCents;
          let lineSale = line.lineSubCents > 0 ? line.lineSubCents : otBase;

          if (bundle?.applyDiscountToNonSubscriptionItems && bundle.discount && otBase > 0) {
            const discount = froyaNormalizeDiscount(bundle.discount, otBase / 100);
            if (discount) {
              lineSale = froyaApplyOtpDiscount(otBase, discount);
            }
          }

          otherSaleCents += lineSale * qty;
        }

        const loopSubscriptionCents =
          subscriptionSaleCents > 0 ? subscriptionSaleCents : Math.max(0, fallbackSub - otherSaleCents);

        return this._applySubBundlePreviewDiscount(
          {
            saleCents: (subscriptionSaleCents > 0 || otherSaleCents > 0)
              ? loopSubscriptionCents + otherSaleCents
              : fallbackSub,
            compareCents: compareCents > 0 ? compareCents : fallbackCmp,
          },
          subBundle,
          loopSubscriptionCents,
        );
      }

      /**
       * Preview SUB bundle % on top of Loop selling-plan subtotal (matches checkout Function).
       * Only the subscription portion is discounted; OTP add-ons stay untouched.
       */
      _applySubBundlePreviewDiscount(totals, subBundle, subscriptionPortionCents) {
        const saleCents = Number(totals?.saleCents) || 0;
        const compareCents = Number(totals?.compareCents) || 0;
        const subPortion = Math.max(0, Number(subscriptionPortionCents) || 0);

        if (!subBundle?.discount || subPortion <= 0) {
          return { saleCents, compareCents };
        }

        const discount = froyaNormalizeDiscount(subBundle.discount, subPortion / 100);
        if (!discount) return { saleCents, compareCents };

        const discountedSubPortion = froyaApplyOtpDiscount(subPortion, discount);
        const otherPortion = Math.max(0, saleCents - subPortion);

        return {
          saleCents: discountedSubPortion + otherPortion,
          compareCents,
        };
      }

      _subCentsForQtyIdx(card, idx) {
        if (idx === 1) return parseInt(card.dataset.nbbbSubQ1 ?? card.dataset.nbbbSubQ0 ?? '0', 10);
        if (idx === 2) return parseInt(card.dataset.nbbbSubQ2 ?? card.dataset.nbbbSubQ0 ?? '0', 10);
        return parseInt(card.dataset.nbbbSubQ0 ?? '0', 10);
      }

      _cmpCentsForQtyIdx(card, idx) {
        if (idx === 1) return parseInt(card.dataset.nbbbCmpQ1 ?? card.dataset.nbbbCmpQ0 ?? '0', 10);
        if (idx === 2) return parseInt(card.dataset.nbbbCmpQ2 ?? card.dataset.nbbbCmpQ0 ?? '0', 10);
        return parseInt(card.dataset.nbbbCmpQ0 ?? '0', 10);
      }

      _otBaseCentsForQtyIdx(card, idx) {
        if (idx === 1) return parseInt(card.dataset.nbbbOtQ1 ?? card.dataset.nbbbOtQ0 ?? '0', 10);
        if (idx === 2) return parseInt(card.dataset.nbbbOtQ2 ?? card.dataset.nbbbOtQ0 ?? '0', 10);
        return parseInt(card.dataset.nbbbOtQ0 ?? '0', 10);
      }

      _otCentsForQtyIdx(card, idx) {
        const base = this._otBaseCentsForQtyIdx(card, idx);
        const bundle = this._getFroyaBundleForCard(card, idx, { subscriptionCheckout: false });
        if (!bundle?.discount || base <= 0) return base;

        const discount = froyaNormalizeDiscount(bundle.discount, base / 100);
        if (!discount) return base;

        const sale = froyaApplyOtpDiscount(base, discount);
        if (sale <= 0 && base > 0) {
          console.warn(
            '[bundle-buy-box] Frøya OTP promo reduced sale to $0 — check app discount (use % e.g. 20, not full retail as fixed $ off)',
            {
              bundleId: bundle.id,
              bundleTitle: bundle.title,
              otpSubtotalCents: base,
              discount: bundle.discount,
            },
          );
        }
        return sale;
      }

      _lineCentsForPurchaseType(card, idx) {
        if (this._isSkinOffer() && this._isSubscriptionPurchase()) {
          return this._subscriptionSkinOfferTotals(card, idx).saleCents;
        }
        if (this._isSubscriptionPurchase()) {
          const base = this._subCentsForQtyIdx(card, idx);
          const subBundle = this._getFroyaSubBundleForCard(card, idx);
          if (!subBundle?.discount || base <= 0) return base;
          const discount = froyaNormalizeDiscount(subBundle.discount, base / 100);
          if (!discount) return base;
          return froyaApplyOtpDiscount(base, discount);
        }
        return this._otCentsForQtyIdx(card, idx);
      }

      _saveCentsForProposalCard(card, idx) {
        if (!card) return 0;
        if (this._isSkinOffer()) {
          const isSub = this._isSubscriptionPurchase();
          const saveKey = isSub ? 'nbbbSaveSubQ0' : 'nbbbSaveOtQ0';
          const fixed = parseInt(card.dataset[saveKey] ?? '0', 10);
          if (fixed > 0) return fixed;
        }
        const cmp = this._cmpCentsForQtyIdx(card, idx);
        const sale = this._lineCentsForPurchaseType(card, idx);
        return cmp > sale ? cmp - sale : 0;
      }

      _setSkinOfferPriceEl(container, cents) {
        if (!container) return;
        let priceWrapper = container.querySelector('.product_price');
        if (!priceWrapper) {
          priceWrapper = document.createElement('span');
          priceWrapper.className = 'product_price';
          container.innerHTML = '';
          container.appendChild(priceWrapper);
        }
        priceWrapper.textContent = nbbbFormatMoney(cents);
      }

      _ensureSkinOfferCompareEl(card) {
        const pricesEl = card.querySelector('.skin-offer-purchase-card__prices, .js-subscribtion__option-price');
        if (!pricesEl) return null;

        let cmpEl = pricesEl.querySelector('.js-subscribtion__compare-at-price');
        if (cmpEl) return cmpEl;

        cmpEl = document.createElement('span');
        cmpEl.className = 'js-subscribtion__compare-at-price is-hidden';
        const priceWrapper = document.createElement('span');
        priceWrapper.className = 'product_price';
        cmpEl.appendChild(priceWrapper);

        const mainEl = pricesEl.querySelector('.js-subscribtion__main-price');
        pricesEl.insertBefore(cmpEl, mainEl || null);
        return cmpEl;
      }

      _updateSkinOfferCardPrices(card, saleCents, compareCents) {
        if (!card) return;

        const mainEl = card.querySelector('.js-subscribtion__main-price');
        const cmpEl = this._ensureSkinOfferCompareEl(card);
        const sale = Number(saleCents) || 0;
        const compare = Number(compareCents) || 0;

        card.dataset.nbbbSyncedSaleCents = String(sale);
        this._setSkinOfferPriceEl(mainEl, sale);

        if (!cmpEl) return;

        if (compare > sale) {
          this._setSkinOfferPriceEl(cmpEl, compare);
          cmpEl.classList.remove('is-hidden');
        } else {
          cmpEl.classList.add('is-hidden');
        }
      }

      _skinOfferDiscountLabelTemplate(badge) {
        return badge?.dataset?.nbbbDiscountLabel?.trim() || '';
      }

      _skinOfferDiscountLabelHasPercentToken(template) {
        return /\{percent\}|\{save_percent\}/i.test(template);
      }

      _formatSkinOfferDiscountLabel(template, pct) {
        return template
          .replace(/\{percent\}/gi, String(pct))
          .replace(/\{save_percent\}/gi, String(pct));
      }

      _hideSkinOfferDiscountBadge(badge) {
        badge.textContent = '';
        badge.hidden = true;
        badge.classList.add('is-hidden');
      }

      _updateSkinOfferDiscountBadge(card, compareCents, saleCents, templateOverride) {
        const badge = card?.querySelector('.c-subscribtion__discount-label');
        if (!badge) return;

        const template = templateOverride !== undefined
          ? String(templateOverride).trim()
          : this._skinOfferDiscountLabelTemplate(badge);

        if (!template) {
          this._hideSkinOfferDiscountBadge(badge);
          return;
        }

        const pct = nbbbPercentOff(compareCents, saleCents);
        const needsPercent = this._skinOfferDiscountLabelHasPercentToken(template);

        if (needsPercent && pct <= 0) {
          this._hideSkinOfferDiscountBadge(badge);
          return;
        }

        const text = this._formatSkinOfferDiscountLabel(template, pct);
        if (!text) {
          this._hideSkinOfferDiscountBadge(badge);
          return;
        }

        badge.textContent = text;
        badge.hidden = false;
        badge.removeAttribute('hidden');
        badge.classList.remove('is-hidden');
      }

      /* Switch-toggle buy box (buybox-switch-toggle.liquid) price line.
         Its markup is server-rendered from the section's own product — on a
         bundle LP that product is a display-only SKU, so every number in it is
         wrong. Overwrite all four with the proposal's real totals, plus the
         congrats amount, then flag the line as priced so CSS can reveal it. */
      _updateBuyboxTogglePrices(subCents, subCompareCents, otCents, otCompareCents) {
        const root = this._contextRoot || document;
        const line = root.querySelector('.c-buybox-toggle__priceline');
        if (!line) return;

        const writeState = (state, saleCents, compareCents) => {
          const wrap = line.querySelector(`.c-buybox-toggle__price--${state}`);
          if (!wrap) return;
          const main = wrap.querySelector('.c-buybox-toggle__price-main');
          if (main && saleCents > 0) main.textContent = nbbbFormatMoney(saleCents);
          const cmp = wrap.querySelector('.c-buybox-toggle__price-compare');
          if (!cmp) return;
          if (compareCents > saleCents && compareCents > 0) {
            cmp.textContent = nbbbFormatMoney(compareCents);
            cmp.hidden = false;
            cmp.removeAttribute('hidden');
          } else {
            cmp.hidden = true;
          }
        };

        writeState('sub', subCents, subCompareCents);
        writeState('otp', otCents, otCompareCents);

        /* Congrats pill: Liquid already substituted {amount} server-side, so
           re-render from the raw template it exposes rather than string-patching
           a formatted price out of the DOM. */
        const congrats = root.querySelector('.c-buybox-toggle__congrats');
        const tpl = congrats?.dataset.bbtCongratsTemplate;
        if (congrats && tpl) {
          const isSub = this._isSubscriptionPurchase();
          const sale = isSub ? subCents : otCents;
          const compare = isSub ? subCompareCents : otCompareCents;
          const saving = Math.max(0, compare - sale);
          const target = congrats.querySelector('span:last-child');
          if (target) {
            if (saving > 0) {
              target.textContent = tpl.replace('{amount}', nbbbFormatMoney(saving));
              congrats.hidden = false;
              congrats.removeAttribute('hidden');
            } else {
              congrats.hidden = true;
            }
          }
        }

        line.dataset.bbtPriced = 'true';
        this._contextRoot?.setAttribute('data-bbt-priced', 'true');
      }

      _syncSkinOfferPurchasePrices() {
        if (!this._isSkinOffer()) return;

        this._ensureFroyaBundles();
        this._ensureFroyaSubBundles();
        this._syncFroyaProposalCards();

        const card = this._getSelectedProposalCard();
        if (!card) return;

        const idx = this.selectedQtyIndex;
        const subTotals = this._subscriptionSkinOfferTotals(card, idx);
        const subCents = subTotals.saleCents;
        const subCompareCents = subTotals.compareCents;
        const otBaseCents = this._otBaseCentsForQtyIdx(card, idx);
        const otCents = this._otCentsForQtyIdx(card, idx);
        const cmpCents = this._cmpCentsForQtyIdx(card, idx);
        const otpAddonBundle = this._getFroyaBundleForCard(card, idx, { subscriptionCheckout: true });
        const froyaSubBundle = this._getFroyaSubBundleForCard(card, idx);
        const otBundle = this._getFroyaBundleForCard(card, idx, { subscriptionCheckout: false });
        const otCompareCents = Math.max(cmpCents, otBaseCents, otCents);

        const root = this._contextRoot || document;
        const subCard = root.querySelector('.skin-offer-purchase-card--subscription');
        const otCard = root.querySelector('.skin-offer-purchase-card--one-time');

        if (nbbbDebugEnabled(this)) {
          console.log('[bundle-buy-box] skin-offer pricing', {
            subCents,
            subCompareCents,
            otpAddonBundle: otpAddonBundle?.id ?? null,
            froyaSubBundle: froyaSubBundle?.id ?? null,
            froyaSubDiscount: froyaSubBundle?.discount ?? null,
            otBaseCents,
            otSaleCents: otCents,
            cmpCents,
            otCompareCents,
            otMatchedBundle: otBundle?.id ?? null,
            otDiscount: otBundle?.discount ?? null,
          });
        }

        this._updateSkinOfferCardPrices(subCard, subCents, subCompareCents);
        this._updateSkinOfferCardPrices(otCard, otCents, otCompareCents);
        this._updateBuyboxTogglePrices(subCents, subCompareCents, otCents, otCompareCents);

        const proposalIdx = card.dataset.nbbbProposalIdx;
        const subLabelTemplate = this.#resolveSubscriptionDiscountLabelTemplate(proposalIdx);
        const otLabelTemplate = this.#resolveOneTimeDiscountLabelTemplate(proposalIdx);

        this._updateSkinOfferDiscountBadge(subCard, subCompareCents, subCents, subLabelTemplate);
        this._updateSkinOfferDiscountBadge(otCard, otCompareCents, otCents, otLabelTemplate);
        this._syncFroyaPromoButton();
      }

      _recurringCentsForQtyIdx(card, idx) {
        const lines = this._parseBundleLineParts(card, idx);
        const subscriptionLines = lines.filter(line => line.sellingPlanId && line.hasRecurringField);
        if (subscriptionLines.length) {
          return subscriptionLines.reduce(
            (sum, line) => sum + line.lineRecurringCents * line.quantity,
            0,
          );
        }

        if (idx === 1) return parseInt(card.dataset.nbbbRecurringQ1 ?? card.dataset.nbbbRecurringQ0 ?? '0', 10);
        if (idx === 2) return parseInt(card.dataset.nbbbRecurringQ2 ?? card.dataset.nbbbRecurringQ0 ?? '0', 10);
        return parseInt(card.dataset.nbbbRecurringQ0 ?? '0', 10);
      }

      _deliveryLabelForQtyIdx(card, idx) {
        const raw = (idx === 1 ? card.dataset.nbbbDlabelQ1 :
                     idx === 2 ? card.dataset.nbbbDlabelQ2 :
                                 card.dataset.nbbbDlabelQ0) || card.dataset.nbbbDlabelQ0 || '';
        return raw.replace(/\b\w/g, c => c.toUpperCase());
      }

      _deliveryLabelRawForQtyIdx(card, idx) {
        const raw = (idx === 1 ? card.dataset.nbbbDlabelQ1 :
                     idx === 2 ? card.dataset.nbbbDlabelQ2 :
                                 card.dataset.nbbbDlabelQ0) || card.dataset.nbbbDlabelQ0 || '';
        return String(raw).trim();
      }

      _ensureRefillInfoEl() {
        if (this._refillInfoEl) return this._refillInfoEl;
        const btn = this._findSubmitButton();
        if (!btn) return null;
        const buttonsDiv =
          btn.closest('.product-form__buttons') ||
          btn.closest('.product-form__buy-buttons') ||
          btn.closest('.c-product-form') ||
          btn.closest('.c-subscribtion');
        if (!buttonsDiv) return null;
        const el = document.createElement('div');
        el.className = 'nbbb__refill-info';
        buttonsDiv.after(el);
        this._refillInfoEl = el;
        return el;
      }

      _hideRefillInfo(el) {
        if (!el) return;
        el.hidden = true;
        el.setAttribute('hidden', '');
        el.setAttribute('aria-hidden', 'true');
        el.className = 'nbbb__refill-info';
        el.classList.add('is-hidden');
        el.innerHTML = '';
      }

      _updateRefillInfo() {
        const el = this._ensureRefillInfoEl();
        if (!el) return;

        if (this._shouldShowSubscriptionRefillFootnote()) {
          this._hideRefillInfo(el);
          return;
        }

        if (!this._isSubscriptionPurchase()) {
          this._hideRefillInfo(el);
          return;
        }
        const card = this._getSelectedProposalCard();
        if (!card) {
          this._hideRefillInfo(el);
          return;
        }

        const idx = this.selectedQtyIndex;
        const units = this.selectedQtyUnitCount;
        const recurringTotal = this._recurringCentsForQtyIdx(card, idx);
        if (!recurringTotal) {
          this._hideRefillInfo(el);
          return;
        }

        const label = this._deliveryLabelForQtyIdx(card, idx);
        const recurringPerUnit = units > 1 ? Math.round(recurringTotal / units) : 0;

        if (this._usesAlternativeRefillLayout()) {
          const noticeHtml = this.#resolveAlternativeRefillNoticeHtml(card, idx);
          if (noticeHtml) {
            el.className = 'nbbb__refill-info nbbb__refill-info--custom';
            el.innerHTML = `<div class="c-subscribtion__button-info-wrapper skin-offer-purchase-card__benefits nbbb__refill-notice-benefits">${noticeHtml}</div>`;
            el.hidden = false;
            el.removeAttribute('hidden');
            el.setAttribute('aria-hidden', 'false');
            el.classList.remove('is-hidden');
            return;
          }
        }

        el.className = 'nbbb__refill-info';

        let html;
        if (this._isSkinOfferContext()) {
          html = `<span class="nbbb__refill-price">${nbbbFormatMoney(recurringTotal)}</span>`;
          html += `<span class="nbbb__refill-text"> ${this._skinOfferRefillText(card)}</span>`;
        } else {
          const everyLbl = this.dataset.refillEveryLabel || '';
          const eachSuf = this.dataset.eachSuffix || '/ea';
          html = `<span class="nbbb__refill-price">${nbbbFormatMoney(recurringTotal)}</span>`;
          html += `<span class="nbbb__refill-text"> ${everyLbl} ${label}</span>`;
          if (recurringPerUnit > 0) {
            html += `<span class="nbbb__refill-ea"> (${nbbbFormatMoney(recurringPerUnit)}${eachSuf})</span>`;
          }
        }
        el.innerHTML = html;
        el.hidden = false;
        el.removeAttribute('hidden');
        el.setAttribute('aria-hidden', 'false');
        el.classList.remove('is-hidden');
      }

      _resolveProductForm() {
        const bySection = document.getElementById(`product-form-${this.sectionId}`);
        if (bySection) return bySection;
        if (this._contextRoot) {
          const featuredForm =
            this._contextRoot.querySelector('#selling-plan-form') ||
            this._contextRoot.querySelector('#custom-product-form');
          if (featuredForm) return featuredForm;
        }
        return document.querySelector('form[data-type="add-to-cart-form"], form[is="product-form"]');
      }

      _findSubmitButton() {
        const bySection = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (bySection) return bySection;

        if (this._contextRoot) {
          const skinBtn = this._contextRoot.querySelector(
            '.js-subscribtion__add-to-cart, .c-subscribtion__add-to-cart, .c-product-form__add-to-cart',
          );
          if (skinBtn) return skinBtn;
        }

        const form = this._resolveProductForm();
        if (!form) return null;

        return (
          form.querySelector('.js-subscribtion__add-to-cart') ||
          form.querySelector('.c-subscribtion__add-to-cart') ||
          form.querySelector('#AddToCart') ||
          form.querySelector('button[type="submit"][data-product-add-to-cart-button]') ||
          form.querySelector('button[type="submit"][name="add"]') ||
          form.querySelector('button[type="submit"]')
        );
      }

      _resolveSubmitButton(bundle) {
        const activeBundle = bundle ?? this._getFroyaBundleForCard(this._getSelectedProposalCard());
        const selector = this._getFroyaTriggerSelector(activeBundle);

        if (this._contextRoot && selector) {
          const match = froyaResolvePromoButton(this._contextRoot, selector);
          if (match) return match;
        }

        return this._findSubmitButton();
      }

      _onContextClick(e) {
        const card = this._getSelectedProposalCard();
        const bundle = card ? this._getFroyaBundleForCard(card) : null;
        const selector = this._getFroyaTriggerSelector(bundle);
        let btn = null;

        if (this._contextRoot && selector) {
          const resolved = froyaResolvePromoButton(this._contextRoot, selector);
          if (resolved && (resolved === e.target || resolved.contains(e.target))) {
            btn = resolved;
          }
        }

        if (!btn) {
          btn = selector
            ? e.target.closest(
                selector
                  .split(',')
                  .map((part) => froyaNormalizeSelector(part.trim()))
                  .filter(Boolean)
                  .join(', '),
              )
            : e.target.closest('.c-product-form__add-to-cart, .js-subscribtion__add-to-cart');
        }

        if (!btn || !this._contextRoot?.contains(btn)) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this._onSubmitButtonClick({
          currentTarget: btn,
          preventDefault: () => e.preventDefault(),
          stopImmediatePropagation: () => e.stopImmediatePropagation(),
        });
      }

      _bindProposalCards() {
        this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-idx]').forEach(card => {
          card.addEventListener('click', () => this._selectProposal(card));
          card.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this._selectProposal(card);
            }
          });
        });
      }

      _findQtyOption(node) {
        if (!node || !this._qtyRoot) return null;
        const opt = node.closest?.('.nbb__delivery-option[data-nbbb-qty-idx]');
        if (!opt || !this._qtyRoot.contains(opt)) return null;
        return opt;
      }

      _onQtyRootClick(event) {
        const opt = this._findQtyOption(event.target);
        if (!opt) return;
        this.#log('qty click', { idx: opt.dataset.nbbbQtyIdx, unitCount: opt.dataset.nbbbUnitCount });
        this._selectQtyOption(opt);
      }

      _onQtyRootChange(event) {
        if (!event.target?.matches?.('[data-nbbb-qty-radio]')) return;
        const opt = this._findQtyOption(event.target);
        if (!opt) return;
        this.#log('qty change', { idx: opt.dataset.nbbbQtyIdx, unitCount: opt.dataset.nbbbUnitCount });
        this._selectQtyOption(opt);
      }

      _bindQtyOptions() {
        this._qtyAbort?.abort();
        if (!this._qtyRoot) {
          this.#log('qty root missing — no quantity listeners bound');
          return;
        }

        this._qtyAbort = new AbortController();
        const { signal } = this._qtyAbort;

        this._qtyRoot.addEventListener('click', this._onQtyRootClick, { capture: true, signal });
        this._qtyRoot.addEventListener('change', this._onQtyRootChange, { signal });
      }

      _hideThemeQuantitySelectors() {
        const root = this._contextRoot;
        if (!root) return;
        root.classList.add('nbbb--hide-theme-quantity');
        const fieldset = root.querySelector('#quantity-fieldset');
        if (!fieldset) return;
        fieldset.hidden = true;
        fieldset.setAttribute('aria-hidden', 'true');
        fieldset.classList.add('is-hidden');
      }

      _applyDefaultQty() {
        const options = this.querySelectorAll('.nbb__bundle-qty .nbb__delivery-option');
        if (!options.length) return;

        const checked = this.querySelector('.nbb__bundle-qty [data-nbbb-qty-radio]:checked');
        const target =
          checked?.closest('.nbb__delivery-option') ||
          this.querySelector('.nbb__bundle-qty .nbb__delivery-option.is-selected') ||
          options[0];

        this._selectQtyOption(target);
      }

      _applyDefaultProposal() {
        const cards = this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-slot]');
        if (!cards.length) return;

        const defaultSlot = String(this.dataset.defaultProposalSlot || '1');
        const target =
          this.querySelector(`.nbb__variant-option[data-nbbb-proposal-slot="${defaultSlot}"]`) || cards[0];

        if (!target || target.classList.contains('is-selected')) return;
        this._selectProposal(target);
      }

      _selectProposal(card) {
        this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-idx]').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        this._refreshAllPrices();
        this._syncPrimaryVariantHint();
        this._syncSkinOfferPurchasePrices();
        this._patchSubmitButtonLabel();
        this._scheduleSkinOfferButtonPatch();
        this._syncGalleryForSelectedProposal();
        this._syncDescriptionForSelectedProposal();
        this._syncInfoForSelectedProposal();
        this._updateRefillInfo();
        this.#syncSubscriptionBenefits();
        this.#syncSubscriptionBenefitsGroupVisibility();
        this._syncFreeGiftsPanel();
        this._syncSubmitButtonGiftTrigger();
        this._syncFroyaPromoButton();
      }

      _selectQtyOption(opt) {
        if (!opt) return;

        this._qtyRoot
          ?.querySelectorAll('.nbb__delivery-option[data-nbbb-qty-idx]')
          .forEach(o => o.classList.remove('is-selected'));
        opt.classList.add('is-selected');

        const radio = opt.querySelector('[data-nbbb-qty-radio]');
        if (radio) radio.checked = true;

        this.#log('qty selected', {
          idx: this.selectedQtyIndex,
          unitCount: this.selectedQtyUnitCount,
        });

        this._refreshAllPrices();
        this._syncSkinOfferPurchasePrices();
        this._patchSubmitButtonLabel();
        this._scheduleSkinOfferButtonPatch();
        this._updateRefillInfo();
        this.#syncSubscriptionBenefits();
        this.#syncSubscriptionBenefitsGroupVisibility();
        this._syncFroyaPromoButton();
      }

      _getSelectedProposalCard() {
        return this.querySelector('.nbb__variant-option[data-nbbb-proposal-idx].is-selected');
      }

      _parseItems(card) {
        const raw = this._itemsRawForQtyIdx(card, this.selectedQtyIndex);
        if (!raw) return [];

        if (raw.includes('~') || raw.includes('|')) {
          return raw
            .split('|')
            .map(part => part.trim())
            .filter(Boolean)
            .map(part => {
              const bits = part.split('~');
              const id = Number(bits[0]);
              const quantity = Number(bits[1] || 1);
              let sellingPlanId = '';
              let lineCmpCents = null;
              if (bits.length >= 4) {
                sellingPlanId = (bits[2] || '').trim();
                const c = parseInt(bits[3], 10);
                if (Number.isFinite(c) && c > 0) lineCmpCents = c;
              } else if (bits.length >= 3) {
                sellingPlanId = (bits[2] || '').trim();
              }
              if (!Number.isFinite(id) || id <= 0) return null;
              return {
                id,
                quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
                sellingPlanId,
                lineCmpCents,
              };
            })
            .filter(Boolean);
        }

        try {
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) return [];
          return parsed
            .map(it => {
              const id = Number(it?.id);
              const quantity = Number(it?.quantity || 1);
              const sellingPlanId = String(it?.selling_plan || it?.sellingPlanId || '').trim();
              if (!Number.isFinite(id) || id <= 0) return null;
              return {
                id,
                quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
                sellingPlanId,
              };
            })
            .filter(Boolean);
        } catch {
          return [];
        }
      }

      _initCustomGallery(el) {
        if (!el) return;
        const run = () => {
          if (window.CustomGallery && typeof window.CustomGallery.init === 'function') {
            window.CustomGallery.init(el);
            return true;
          }
          return false;
        };
        if (run()) return;
        let n = 0;
        const t = setInterval(() => {
          n += 1;
          if (run() || n > 120) clearInterval(t);
        }, 50);
      }

      #resolveNativeGallery() {
        if (this._mediaGallery) return this._mediaGallery;
        if (!this._contextRoot) return null;

        this._mediaGallery =
          this._contextRoot.querySelector('.js-featured-product__gallery') ||
          this._contextRoot.querySelector('.s-featured-product__gallery') ||
          document.getElementById(`MediaGallery-${this.sectionId}`) ||
          null;
        return this._mediaGallery;
      }

      #destroyCustomGalleryNode(el) {
        if (!el) return;
        if (window.CustomGallery?.destroy) {
          window.CustomGallery.destroy(el);
        }
        el.remove();
      }

      #removeInjectedCustomGalleries() {
        if (!this._mediaWrapper) return;

        this._mediaWrapper.querySelectorAll('.custom-gallery').forEach((el) => {
          if (el.closest('[data-nbbb-ssr-gallery]')) return;
          this.#destroyCustomGalleryNode(el);
        });

        this._dynamicCustomGallery = null;
      }

      #setSsrGalleryVisible(visible) {
        if (!this._ssrGalleryWrapper) return;

        const ssrGallery = this._ssrGalleryWrapper.querySelector('.custom-gallery');
        this._ssrGalleryWrapper.classList.toggle('nbbb__gallery-hidden', !visible);

        if (!ssrGallery) return;

        if (visible) {
          ssrGallery.dataset.nbbbGalleryActive = 'true';
          return;
        }

        delete ssrGallery.dataset.nbbbGalleryActive;
      }

      #setActiveCustomGallery(galleryEl) {
        if (!this._mediaWrapper || !galleryEl) return;

        this._mediaWrapper.querySelectorAll('.custom-gallery').forEach((el) => {
          if (el === galleryEl) {
            el.dataset.nbbbGalleryActive = 'true';
            return;
          }

          delete el.dataset.nbbbGalleryActive;
        });
      }

      #expectedGalleryVariant(proposalIdx) {
        if (
          this._isSubscriptionPurchase() &&
          this.querySelector(`template[data-nbbb-gallery-template-sub="${proposalIdx}"]`)
        ) {
          return 'sub';
        }
        return 'onetime';
      }

      #reuseSsrGallery(proposalIdx, tpl) {
        if (!tpl) return false;

        const ssrGallery = this._ssrGalleryWrapper?.querySelector('.custom-gallery');
        if (!ssrGallery) return false;
        if (String(ssrGallery.dataset.nbbbProposalIdx) !== String(proposalIdx)) return false;
        if (ssrGallery.dataset.nbbbGalleryVariant !== this.#expectedGalleryVariant(proposalIdx)) {
          return false;
        }

        this.#removeInjectedCustomGalleries();
        this.#setSsrGalleryVisible(true);
        this.#setActiveCustomGallery(ssrGallery);
        this._hideNativeGalleries();
        this._initCustomGallery(ssrGallery);
        return true;
      }

      #setGalleryWrapperMode(mode) {
        if (!this._mediaWrapper) return;

        const isCustom = mode === 'custom';
        this._mediaWrapper.classList.toggle('nbbb--custom-gallery-active', isCustom);
        this._mediaWrapper.classList.toggle('nbbb--native-gallery-active', !isCustom);
      }

      #resizeNativeFlickityGalleries() {
        if (!window.Flickity) return;

        const mainEl = this._contextRoot?.querySelector('.js-featured-product__gallery');
        const thumbsEl = this._contextRoot?.querySelector('.js-featured-product__thumbnails');
        const main = mainEl ? window.Flickity.data(mainEl) : null;
        const thumbs = thumbsEl ? window.Flickity.data(thumbsEl) : null;

        requestAnimationFrame(() => {
          main?.resize();
          thumbs?.resize();
        });
      }

      #cloneGalleryFromTemplate(tpl) {
        if (!tpl?.content) return null;

        const source =
          tpl.content.querySelector('.custom-gallery') || tpl.content.firstElementChild;
        if (!source?.classList?.contains('custom-gallery')) return null;

        return source.cloneNode(true);
      }

      _hideNativeGalleries() {
        this.#setGalleryWrapperMode('custom');

        if (!this._nativeHiddenWrapper) {
          this.#resolveNativeGallery();
          this._mediaGallery?.classList.add('nbbb__gallery-hidden');
          if (
            this._originalCustomGallery &&
            !this._originalCustomGallery.dataset.nbbbGalleryActive
          ) {
            this._originalCustomGallery.classList.add('nbbb__gallery-hidden');
          }
          this._contextRoot
            ?.querySelector('.js-featured-product__thumbnails')
            ?.classList.add('nbbb__gallery-hidden');
        }
      }

      _showNativeGalleries() {
        this.#setGalleryWrapperMode('native');
        this.#setSsrGalleryVisible(false);
        if (this._nativeHiddenWrapper) {
          this._nativeHiddenWrapper.classList.remove('nbbb__gallery-hidden');
          return;
        }

        this.#resolveNativeGallery();
        this._mediaGallery?.classList.remove('nbbb__gallery-hidden');
        if (
          this._originalCustomGallery &&
          !this._originalCustomGallery.dataset.nbbbGalleryActive
        ) {
          this._originalCustomGallery.classList.remove('nbbb__gallery-hidden');
        }
        this._contextRoot
          ?.querySelector('.js-featured-product__thumbnails')
          ?.classList.remove('nbbb__gallery-hidden');
        this.#resizeNativeFlickityGalleries();
      }

      _syncGalleryForSelectedProposal() {
        const card = this._getSelectedProposalCard();
        if (!card || !this._mediaWrapper) return;

        const proposalIdx = card.dataset.nbbbProposalIdx;
        const tpl = this._getGalleryTemplate(proposalIdx);

        if (this.#reuseSsrGallery(proposalIdx, tpl)) return;

        if (proposalIdx === '0' && this._ssrGalleryWrapper && !tpl) {
          this.#removeInjectedCustomGalleries();
          this.#setSsrGalleryVisible(true);
          this.#setActiveCustomGallery(this._ssrGalleryWrapper.querySelector('.custom-gallery'));
          this._hideNativeGalleries();
          return;
        }

        const galleryNode = tpl ? this.#cloneGalleryFromTemplate(tpl) : null;

        if (!galleryNode) {
          this.#removeInjectedCustomGalleries();
          if (proposalIdx === '0' && this._ssrGalleryWrapper) {
            this.#setSsrGalleryVisible(true);
            this.#setActiveCustomGallery(this._ssrGalleryWrapper.querySelector('.custom-gallery'));
            this._hideNativeGalleries();
            return;
          }

          this._showNativeGalleries();
          return;
        }

        this.#removeInjectedCustomGalleries();

        galleryNode.id = `CustomGallery-${this.sectionId}-${proposalIdx}`;
        galleryNode.dataset.nbbbProposalIdx = proposalIdx;
        galleryNode.dataset.nbbbGalleryVariant = this.#expectedGalleryVariant(proposalIdx);

        this.#setSsrGalleryVisible(false);

        const nativeGallery = this.#resolveNativeGallery();
        if (nativeGallery?.parentNode) {
          nativeGallery.parentNode.insertBefore(galleryNode, nativeGallery);
        } else {
          this._mediaWrapper.prepend(galleryNode);
        }

        this._dynamicCustomGallery = galleryNode;
        this.#setActiveCustomGallery(galleryNode);
        this._hideNativeGalleries();
        this._initCustomGallery(galleryNode);
      }

      #getProposalDescriptionHtml(proposalIdx) {
        const tpl = this.querySelector(`template[data-nbbb-desc-template="${proposalIdx}"]`);
        return tpl ? tpl.innerHTML.trim() : '';
      }

      #syncSubheadline(descriptionHtml) {
        if (!this._subheadlineEl) return;

        const content = descriptionHtml || this._subheadlineFallback;
        if (content) {
          this._subheadlineEl.innerHTML = content;
          this._subheadlineEl.hidden = false;
          return;
        }

        this._subheadlineEl.innerHTML = '';
        this._subheadlineEl.hidden = true;
      }

      #getProposalBenefitsHtml(proposalIdx) {
        const tpl = this.querySelector(`template[data-nbbb-benefits-template="${proposalIdx}"]`);
        return tpl ? tpl.innerHTML.trim() : '';
      }

      #getProposalSubscriptionBenefitsHtml(proposalIdx) {
        const tpl = this.querySelector(
          `template[data-nbbb-subscription-benefits-template="${proposalIdx}"]`,
        );
        return tpl ? tpl.innerHTML.trim() : '';
      }

      #readDiscountLabelTemplateValue(tpl) {
        if (!tpl) return null;
        return tpl.dataset.nbbbDiscountLabelValue?.trim() ?? '';
      }

      #getProposalSubscriptionDiscountLabelTemplate(proposalIdx) {
        const tpl = this.querySelector(
          `template[data-nbbb-subscription-discount-label-template="${proposalIdx}"]`,
        );
        return this.#readDiscountLabelTemplateValue(tpl);
      }

      #getProposalOneTimeDiscountLabelTemplate(proposalIdx) {
        const tpl = this.querySelector(
          `template[data-nbbb-one-time-discount-label-template="${proposalIdx}"]`,
        );
        return this.#readDiscountLabelTemplateValue(tpl);
      }

      #buildSubscriptionDiscountLabelFallback() {
        const tpl = this.querySelector('template[data-nbbb-subscription-discount-label-template="fallback"]');
        return this.#readDiscountLabelTemplateValue(tpl) ?? '';
      }

      #buildOneTimeDiscountLabelFallback() {
        const tpl = this.querySelector('template[data-nbbb-one-time-discount-label-template="fallback"]');
        return this.#readDiscountLabelTemplateValue(tpl) ?? '';
      }

      #resolveSubscriptionDiscountLabelTemplate(proposalIdx) {
        const proposalValue = this.#getProposalSubscriptionDiscountLabelTemplate(proposalIdx);
        if (proposalValue !== null) return proposalValue;
        return this.#buildSubscriptionDiscountLabelFallback();
      }

      #resolveOneTimeDiscountLabelTemplate(proposalIdx) {
        const proposalValue = this.#getProposalOneTimeDiscountLabelTemplate(proposalIdx);
        if (proposalValue !== null) return proposalValue;
        return this.#buildOneTimeDiscountLabelFallback();
      }

      #buildSubscriptionBenefitsFallbackHtml() {
        const tpl = this.querySelector('template[data-nbbb-subscription-benefits-template="fallback"]');
        if (tpl?.innerHTML.trim()) return tpl.innerHTML.trim();

        return `<ul>
          <li><strong>Save {save}</strong> on your first delivery</li>
          <li>Delivered <strong>every {delivery}</strong></li>
          <li>Easily <strong>skip</strong>, <strong>pause</strong>, or <strong>cancel</strong> anytime</li>
        </ul>`;
      }

      #stripSubscriptionRefillNote(html) {
        if (!html) return '';
        return html
          .replace(/<p[^>]*class="[^"]*skin-offer-purchase-card__refill-note[^"]*"[^>]*>[\s\S]*?<\/p>/gi, '')
          .trim();
      }

      #subscriptionSaveCentsForCard(card, idx) {
        if (!card) return 0;
        const totals = this._subscriptionSkinOfferTotals(card, idx);
        return totals.compareCents > totals.saleCents
          ? totals.compareCents - totals.saleCents
          : 0;
      }

      #buildSubscriptionBenefitRefillLine(card, idx) {
        if (!card) return '';
        const recurringTotal = this._recurringCentsForQtyIdx(card, idx);
        if (!recurringTotal) return '';
        return `${nbbbFormatMoney(recurringTotal)} ${this._skinOfferRefillText(card)}`;
      }

      #applySubscriptionBenefitTokens(html, card, idx) {
        if (!html || !card) return html || '';

        const saveCents = this.#subscriptionSaveCentsForCard(card, idx);
        const subCents = this._subscriptionSkinOfferTotals(card, idx).saleCents;
        const recurringCents = this._recurringCentsForQtyIdx(card, idx);
        const deliveryLabel = this._deliveryLabelRawForQtyIdx(card, idx);

        const tokens = {
          save: saveCents > 0 ? nbbbFormatMoney(saveCents) : '',
          first_order: subCents > 0 ? nbbbFormatMoney(subCents) : '',
          recurring: recurringCents > 0 ? nbbbFormatMoney(recurringCents) : '',
          delivery: deliveryLabel,
          refill_line: this.#buildSubscriptionBenefitRefillLine(card, idx),
        };

        let output = html;
        for (const [key, value] of Object.entries(tokens)) {
          output = output.replace(new RegExp(`\\{${key}\\}`, 'gi'), value);
        }
        return output;
      }

      #syncSubscriptionRefillNote(card, idx) {
        const el = this._subscriptionRefillNoteEl;
        if (!el) return;

        if (!this._shouldShowSubscriptionRefillFootnote()) {
          el.textContent = '';
          el.hidden = true;
          el.setAttribute('hidden', '');
          return;
        }

        const line = this.#buildSubscriptionBenefitRefillLine(card, idx);
        if (line) {
          el.textContent = line;
          el.hidden = false;
          el.removeAttribute('hidden');
          return;
        }

        el.textContent = '';
        el.hidden = true;
        el.setAttribute('hidden', '');
      }

      #syncSubscriptionBenefits() {
        if (!this._subscriptionBenefitsEl && !this._subscriptionRefillNoteEl) return;

        const card = this._getSelectedProposalCard();
        if (!card) return;

        const proposalIdx = card.dataset.nbbbProposalIdx;
        const idx = this.selectedQtyIndex;

        if (this._subscriptionBenefitsEl) {
          const rawContent =
            this.#getProposalSubscriptionBenefitsHtml(proposalIdx) ||
            this.#buildSubscriptionBenefitsFallbackHtml();
          const listHtml = this.#stripSubscriptionRefillNote(rawContent);
          this._subscriptionBenefitsEl.innerHTML = this.#applySubscriptionBenefitTokens(listHtml, card, idx);
        }

        this.#syncSubscriptionRefillNote(card, idx);
        this.#syncSubscriptionBenefitsGroupVisibility();
      }

      #syncSubscriptionBenefitsGroupVisibility() {
        const group = this._subscriptionBenefitsGroupEl;
        if (!group) return;

        const hideForOneTime =
          this.dataset.hideSubscriptionBenefitsOneTime === 'true' &&
          !this._isSubscriptionPurchase();

        group.classList.toggle('nbbb--benefits-group-collapsed', hideForOneTime);

        if (hideForOneTime) {
          group.setAttribute('aria-hidden', 'true');
          return;
        }

        group.hidden = false;
        group.removeAttribute('hidden');
        group.removeAttribute('aria-hidden');
      }

      #buildBenefitsFallbackHtml() {
        if (!this._benefitsEl) return '';

        const lines = [
          this._benefitsEl.dataset.nbbbBenefitFallback1,
          this._benefitsEl.dataset.nbbbBenefitFallback2,
          this._benefitsEl.dataset.nbbbBenefitFallback3,
        ].filter((line) => line?.trim());

        if (!lines.length) return '';

        return `<div class="s-featured-product-bundles__benefits-rte rte"><ul role="list">${lines
          .map((line) => {
            const span = document.createElement('span');
            span.textContent = line.trim();
            return `<li role="listitem">${span.innerHTML}</li>`;
          })
          .join('')}</ul></div>`;
      }

      #syncBenefits(proposalIdx) {
        if (!this._benefitsEl) return;

        const content = this.#getProposalBenefitsHtml(proposalIdx) || this.#buildBenefitsFallbackHtml();
        if (content) {
          this._benefitsEl.innerHTML = content;
          this._benefitsEl.hidden = false;
          return;
        }

        this._benefitsEl.innerHTML = '';
        this._benefitsEl.hidden = true;
      }

      _syncDescriptionForSelectedProposal() {
        const card = this._getSelectedProposalCard();
        if (!card) return;

        const proposalIdx = card.dataset.nbbbProposalIdx;
        const descriptionHtml = this.#getProposalDescriptionHtml(proposalIdx);

        if (this._descEl) {
          if (descriptionHtml) {
            this._descEl.innerHTML = descriptionHtml;
          } else if (this._originalDesc !== null) {
            this._descEl.innerHTML = this._originalDesc;
          }
        }

        this.#syncSubheadline(descriptionHtml);
        this.#syncBenefits(proposalIdx);
        this.#syncSubscriptionBenefits();
        this.#syncSubscriptionBenefitsGroupVisibility();
      }

      _syncInfoForSelectedProposal() {
        if (!this._infoEl) return;
        const card = this._getSelectedProposalCard();
        if (!card) return;
        const proposalIdx = card.dataset.nbbbProposalIdx;
        const tpl = this.querySelector(`template[data-nbbb-info-template="${proposalIdx}"]`);
        this._infoEl.innerHTML = tpl ? tpl.innerHTML : '';
        this._infoEl.hidden = !tpl || !tpl.innerHTML.trim();
        if (!this._infoEl.hidden) this._highlightFreeWords(this._infoEl);
      }

      _highlightFreeWords(el) {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        const nodes = [];
        let n;
        while ((n = walker.nextNode())) nodes.push(n);
        nodes.forEach(node => {
          if (!/FREE/i.test(node.textContent)) return;
          const frag = document.createDocumentFragment();
          node.textContent.split(/(FREE)/i).forEach(part => {
            if (/^FREE$/i.test(part)) {
              const span = document.createElement('span');
              span.className = 'nbbb__info-free';
              span.textContent = part;
              frag.appendChild(span);
            } else {
              frag.appendChild(document.createTextNode(part));
            }
          });
          node.parentNode.replaceChild(frag, node);
        });
      }

      _refreshAllPrices() {
        const card = this._getSelectedProposalCard();
        if (!card) return;

        const idx = this.selectedQtyIndex;
        const units = this.selectedQtyUnitCount;

        this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-idx]').forEach(c => {
          const cSub = this._subCentsForQtyIdx(c, idx);
          const cCmp = this._cmpCentsForQtyIdx(c, idx);
          const cSubPerUnit = units > 1 ? Math.round(cSub / units) : cSub;
          const cmpPerUnit = units > 1 ? Math.round(cCmp / units) : cCmp;

          const priceEl = c.querySelector('[data-nbbb-card-price]');
          if (priceEl) {
            if (units > 1) {
              priceEl.innerHTML = nbbbFormatMoney(cSubPerUnit) + '<span class="nbb__vp-each"> /each</span>';
            } else {
              priceEl.textContent = nbbbFormatMoney(cSubPerUnit);
            }
          }

          const cmpEl = c.querySelector('[data-nbbb-card-compare]');
          if (cmpEl) {
            cmpEl.textContent = nbbbFormatMoney(cmpPerUnit);
            cmpEl.classList.toggle('is-hidden', cmpPerUnit <= cSubPerUnit);
          }

          const saveEl = c.querySelector('[data-nbbb-card-save]');
          if (saveEl) {
            const saveCents = this._isSkinOffer()
              ? this._saveCentsForProposalCard(c, idx)
              : cmpPerUnit - cSubPerUnit;
            const savePrefix = this.dataset.savePrefix || 'Save';
            if (saveCents > 0) {
              saveEl.textContent = `${savePrefix} ${nbbbFormatMoney(saveCents)}`;
              saveEl.classList.remove('is-hidden');
            } else {
              saveEl.textContent = '';
              saveEl.classList.add('is-hidden');
            }
          }
        });

        const subLine = this._subCentsForQtyIdx(card, idx);
        const cmpLine = this._cmpCentsForQtyIdx(card, idx);

        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (gallery && typeof gallery.setSavePercent === 'function') {
          gallery.setSavePercent(nbbbPercentOff(cmpLine, subLine));
        }

        const items = this._parseItems(card);
        const primaryVid = items.length ? String(items[0].id) : '';
        const activePriceEl = card.querySelector('[data-nbbb-card-price]');
        const activeCmpEl = card.querySelector('[data-nbbb-card-compare]');

        document.dispatchEvent(
          new CustomEvent('bundle-buy-box:change', {
            detail: {
              variantId: primaryVid,
              displayPrice: activePriceEl?.textContent?.trim() || '',
              comparePrice: cmpLine > subLine ? (activeCmpEl?.textContent?.trim() || '') : '',
              bundleQtyIdx: idx,
              proposalSlot: card.dataset.nbbbProposalSlot || '',
            },
          })
        );

        this._syncSkinOfferPurchasePrices();
        this._syncFreeGiftsPanel();
        this._syncSubmitButtonGiftTrigger();
      }

      _getFreeGiftsTemplate(card) {
        if (!card) return null;

        const slot = String(card.dataset.nbbbProposalSlot || '').trim();
        if (slot) {
          const bySlot = this.querySelector(`template[data-nbbb-gifts-template="${slot}"]`);
          if (bySlot) return bySlot;
        }

        const proposalIdx = String(card.dataset.nbbbProposalIdx || '').trim();
        if (proposalIdx) {
          const byIdx = this.querySelector(
            `template[data-nbbb-gifts-proposal-idx="${proposalIdx}"]`
          );
          if (byIdx) return byIdx;
        }

        return null;
      }

      _syncFreeGiftsPanel() {
        const panel =
          this._contextRoot?.querySelector('[data-nbbb-free-gifts-panel]') ||
          document.querySelector('[data-nbbb-free-gifts-panel]');
        if (!panel) return;

        if (!this._isSkinOffer()) {
          panel.hidden = true;
          panel.replaceChildren();
          return;
        }

        if (!this._shouldIncludeProposalFreeGifts()) {
          panel.hidden = true;
          panel.replaceChildren();
          return;
        }

        const card = this._getSelectedProposalCard();
        const giftIds = this._getFreeGiftVariantIds(card);

        if (!card || giftIds.length === 0) {
          panel.hidden = true;
          panel.replaceChildren();
          return;
        }

        const tpl = this._getFreeGiftsTemplate(card);
        if (!tpl?.content?.childNodes?.length) {
          panel.hidden = true;
          panel.replaceChildren();
          return;
        }

        panel.replaceChildren(tpl.content.cloneNode(true));
        panel.hidden = false;
      }

      /**
       * Keeps theme listeners happy: set hidden variant id to first bundle line item.
       */
      _syncPrimaryVariantHint() {
        const card = this._getSelectedProposalCard();
        const items = this._parseItems(card);
        const vid = items.length ? String(items[0].id) : '';
        if (!vid) return;

        const form = document.getElementById(`product-form-${this.sectionId}`);
        const idInput = form?.querySelector('input[name="id"]');
        if (!idInput) return;
        if (String(idInput.value) === vid) return;
        idInput.value = vid;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      _isPurchaseBlocked(card) {
        if (this.dataset.purchaseBlocked === 'true') return true;
        const selected = card || this._getSelectedProposalCard();
        return selected?.dataset.nbbbPurchaseBlocked === 'true';
      }

      _getSoldOutLabel() {
        return (
          (window.themeVariables &&
            window.themeVariables.strings &&
            window.themeVariables.strings.productFormSoldOut) ||
          'Sold out'
        );
      }

      _setSubmitButtonSoldOut(btn) {
        if (!btn) return;
        btn.setAttribute('disabled', 'disabled');
        btn.removeAttribute('aria-busy');
        const label = this._getSoldOutLabel();
        const btnSpan =
          btn.querySelector('.js-subscribtion__button-text') ||
          btn.querySelector('.c-subscribtion__button-text') ||
          btn.querySelector('.loader-button__text .product-card__button-text') ||
          btn.querySelector('.loader-button__text') ||
          btn.querySelector('.c-product-form__button-text') ||
          btn.querySelector('span');
        if (btnSpan) {
          btnSpan.textContent = label;
        } else {
          btn.textContent = label;
        }
        const priceWrap = btn.querySelector('.js-subscribtion__price-wrapper');
        if (priceWrap) priceWrap.hidden = true;
      }

      _patchSubmitButtonLabel() {
        if (this._patchingSubmitLabel) return;

        const btn = this._findSubmitButton();
        if (!btn) return;

        const card = this._getSelectedProposalCard();
        if (!card) return;

        if (this._isPurchaseBlocked(card)) {
          this._setSubmitButtonSoldOut(btn);
          return;
        }

        const q = this.selectedQtyIndex;
        const line = this._lineCentsForPurchaseType(card, q);
        if (line <= 0) return;

        if (this._isSkinOffer()) {
          // The label the section rendered wins when one was set. Without this
          // the hardcoded string below silently overwrote a configured
          // "Add to cart" text every time the price synced.
          const prefix = (this.dataset.atcLabel || '').trim() || 'Add to cart';
          const label = `${prefix} \u2014 ${nbbbFormatMoney(line)}`;
          const btnSpan =
            btn.querySelector('.js-subscribtion__button-text') ||
            btn.querySelector('.c-subscribtion__button-text') ||
            btn.querySelector('.loader-button__text') ||
            btn.querySelector('.c-product-form__button-text');
          if (btnSpan?.textContent === label) {
            const priceWrap = btn.querySelector('.js-subscribtion__price-wrapper');
            if (priceWrap) priceWrap.hidden = true;
            return;
          }

          this._patchingSubmitLabel = true;
          try {
            if (btnSpan) {
              btnSpan.textContent = label;
            } else {
              btn.textContent = label;
            }
            const priceWrap = btn.querySelector('.js-subscribtion__price-wrapper');
            if (priceWrap) priceWrap.hidden = true;
          } finally {
            this._patchingSubmitLabel = false;
          }
          return;
        }

        const btnSpan =
          btn.querySelector('.loader-button__text') ||
          btn.querySelector('.c-product-form__button-text') ||
          btn.querySelector('.js-subscribtion__button-text') ||
          btn.querySelector('.c-subscribtion__button-text') ||
          btn.querySelector('span');
        if (!btnSpan) return;

        const atc =
          (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.productFormAddToCart) ||
          'Add to cart';
        btnSpan.textContent = `${atc} \u2013 ${nbbbFormatMoney(line)}`;
      }

      _onDocSubmit(e) {
        const form = e.target;
        const isProductForm =
          form &&
          (form.getAttribute('data-type') === 'add-to-cart-form' ||
            form.getAttribute('is') === 'product-form' ||
            form.id === 'selling-plan-form' ||
            form.id === 'custom-product-form');
        if (!isProductForm) return;
        const targetForm = this._resolveProductForm();
        if (!targetForm || form !== targetForm) return;
        if (this._contextRoot && !this._contextRoot.contains(form)) return;

        if (this._isSubmittingBundle) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        if (this._isPurchaseBlocked()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        e.preventDefault();
        e.stopImmediatePropagation();
        this._submitBundle(form, this._resolveSubmitButton());
      }

      _onSubmitButtonClick(e) {
        const btn = e.currentTarget;
        if (!btn) return;
        if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
        const form = this._resolveProductForm();
        if (!form) return;

        const card = this._getSelectedProposalCard();
        if (!card) return;
        if (this._isPurchaseBlocked(card)) return;

        const items = this._parseItems(card);
        if (!items.length) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        this._submitBundle(form, btn);
      }

      /* Back from checkout via the back/forward cache: the page comes back
         frozen exactly as it left. Two things in it are stale:
         - the button — the checkout redirect leaves it disabled + aria-busy on
           purpose (no flash of an enabled button while the page unloads), so
           it came back greyed out and dead;
         - the purchase type — the last pick (e.g. one-time) is still checked.
         Unlock the button and put the purchase type back to the server-rendered
         default (defaultChecked = the `checked` attribute), dispatching a real
         'change' so c-subscription, this buy box and the switch toggle all
         re-sync. The cart itself is re-read on every pageshow by
         js-variables.liquid, so the restore stays instant — no reload. */
      _onPageShow(event) {
        if (!event.persisted) return;
        this._isSubmittingBundle = false;
        this._setSubmitButtonLoading(this._submitBtn || this._resolveSubmitButton(), false);

        const form = this._resolveProductForm();
        const radios = form ? form.querySelectorAll('input[name="purchase_type"][type="radio"]') : [];
        const initial = Array.from(radios).find((radio) => radio.defaultChecked);
        if (initial && !initial.checked) {
          initial.checked = true;
          initial.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      _generateBundleId() {
        return '_b' + Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
      }

      _setSubmitButtonLoading(btn, loading) {
        if (!btn) return;
        if (loading) {
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-busy', 'true');
          return;
        }
        btn.removeAttribute('aria-busy');
        if (this._isPurchaseBlocked()) {
          btn.setAttribute('disabled', 'disabled');
          return;
        }
        btn.removeAttribute('disabled');
      }

      async _fetchCart(options = {}) {
        try {
          const url = options.sections
            ? `${nbbbCartJsUrl()}?sections=${encodeURIComponent(options.sections)}`
            : nbbbCartJsUrl();
          const res = await fetch(url, {
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });
          if (!res.ok) return { items: [] };
          return await res.json();
        } catch (_) {
          return { items: [] };
        }
      }

      _mergeCartSections(cart, sectionsFromAdd) {
        if (!sectionsFromAdd || typeof sectionsFromAdd !== 'object') return cart;
        cart.sections = { ...(cart.sections || {}), ...sectionsFromAdd };
        return cart;
      }

      _variantIdsAlreadyInCart(cart) {
        const ids = new Set();
        (cart?.items || []).forEach((item) => {
          const vid = Number(item.variant_id);
          if (Number.isFinite(vid) && vid > 0) ids.add(vid);
        });
        return ids;
      }

      async _submitBundle(form, submitBtn) {
        if (this._isSubmittingBundle) return;
        const card = this._getSelectedProposalCard();
        if (!card) return;
        if (this._isPurchaseBlocked(card)) return;

        const freeGiftIds = this._getFreeGiftVariantIds(card);
        const cartPromise = freeGiftIds.length
          ? this._fetchCart()
          : Promise.resolve({ items: [] });

        if (!this._froyaBundles?.length && !this._froyaSubBundles?.length) {
          await (this._froyaConfigProxyPromise || this._loadFroyaConfigFromProxy());
        }

        const items = this._parseItems(card);
        if (!items.length) return;

        const cart = await cartPromise;
        const variantIdsInCart = this._variantIdsAlreadyInCart(cart);

        const bundleId = this._generateBundleId();
        const isSubscription = this._isSubscriptionPurchase();
        const unitMultiplier = this.selectedQtyUnitCount;

        const payloadItems = [];
        const linesByKey = new Map();
        items.forEach(it => {
          const id = Number(it.id);
          if (!Number.isFinite(id) || id <= 0) return;
          const quantity = (Number(it.quantity || 1) || 1) * unitMultiplier;
          const sellingPlanId = isSubscription ? String(it.sellingPlanId || '').trim() : '';
          const key = isSubscription ? `${id}::${sellingPlanId}` : String(id);
          const cmpUnit = Number(it.lineCmpCents);
          const cmpAdd = Number.isFinite(cmpUnit) && cmpUnit > 0 ? Math.round(cmpUnit * quantity) : 0;
          const current = linesByKey.get(key) || {
            id,
            quantity: 0,
            selling_plan: sellingPlanId,
            bundleCmpSum: 0,
          };
          current.quantity += quantity;
          current.bundleCmpSum += cmpAdd;
          linesByKey.set(key, current);
        });
        const froyaBundle = this._getFroyaBundleForCard(card, this.selectedQtyIndex, {
          subscriptionCheckout: isSubscription,
        });
        const froyaSubBundle = isSubscription
          ? this._getFroyaSubBundleForCard(card, this.selectedQtyIndex)
          : null;

        linesByKey.forEach(line => {
          const out = {
            id: line.id,
            quantity: line.quantity,
            properties: {
              _bundle_id: bundleId,
              /* Which LP the bundle was added from. Lets the cart lock quantity
                 for bundle lines without hardcoding bundle UUIDs — see
                 mini-cart-form.liquid / main-cart.liquid. */
              [FROYA_ATTR_TEMPLATE]: this._getFroyaTemplate(),
            },
          };
          /* This buy box adds its own free gift, in THIS payload. The gift
             auto-add engine re-derives gifts from /cart.js on every refresh, so
             without a marker it would add a second copy of the same product a
             beat later. `_froya_no_gift` is the engine's own kill switch
             (gift-autoadd-extension.js, isGiftSuppressedLine): a paid line
             carrying it is skipped as a rule trigger. It has to be a LINE
             PROPERTY — clearing the button's data-gift-trigger only skips the
             inline fast path, and the reconciler puts the gift straight back. */
          if (this.dataset.froyaNoGift === 'true') {
            out.properties._froya_no_gift = '1';
          }
          const tagFroyaBundle = froyaShouldTagBundleLine(
            froyaBundle,
            line.id,
            isSubscription,
            Boolean(line.selling_plan),
          );
          if (tagFroyaBundle) {
            out.properties[FROYA_ATTR_BUNDLE_ID] = froyaBundle.id;
            this.#log('line property', {
              key: FROYA_ATTR_BUNDLE_ID,
              value: froyaBundle.id,
              subscription: isSubscription,
            });
          }
          if (froyaSubBundle?.id && line.selling_plan) {
            out.properties[FROYA_ATTR_SUB_BUNDLE_ID] = froyaSubBundle.id;
            this.#log('line property', {
              key: FROYA_ATTR_SUB_BUNDLE_ID,
              value: froyaSubBundle.id,
              subscription: true,
            });
          }
          if (line.bundleCmpSum > 0) {
            out.properties._nbbb_cmp = String(Math.round(line.bundleCmpSum));
            out.properties._nbbb_cmp_q = String(line.quantity);
          }
          if (isSubscription && line.selling_plan) {
            out.selling_plan = Number(line.selling_plan) || line.selling_plan;
          }
          payloadItems.push(out);
        });

        freeGiftIds.forEach((freeGiftId) => {
          const giftVariantId = Number(freeGiftId);
          if (!Number.isFinite(giftVariantId) || giftVariantId <= 0) return;
          if (variantIdsInCart.has(giftVariantId)) return;

          payloadItems.push({
            id: giftVariantId,
            quantity: 1,
            properties: {
              _bundle_id: bundleId,
              _nbbb_free_gift: 'true',
            },
          });
        });

        if (!payloadItems.length) return;

        const body = {
          items: payloadItems,
          sections: ['mini-cart'],
          sections_url: window.location.pathname,
        };

        this._isSubmittingBundle = true;
        let redirectingToCheckout = false;
        this._setSubmitButtonLoading(submitBtn, true);

        fetch(nbbbCartAddJsUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then(async (r) => {
            const response = await r.json();
            if (!r.ok || response.status) {
              form.dispatchEvent(
                new CustomEvent('cart-notification:show', {
                  bubbles: true,
                  cancelable: true,
                  detail: {
                    status: 'error',
                    error: response.description || response.message || '',
                  },
                })
              );
              return;
            }

            const firstVariant = response.items ? response.items[0] : response;

            form.dispatchEvent(
              new CustomEvent('variant:added', {
                bubbles: true,
                detail: { variant: firstVariant },
              })
            );

            const addedLines = Array.isArray(response.items) ? response.items : [response];
            if (window.BundleCart?.registerBundle) {
              window.BundleCart.registerBundle(bundleId, addedLines);
            }

            const cartContent = this._mergeCartSections(
              await this._fetchCart({ sections: 'mini-cart' }),
              response.sections
            );

            document.documentElement.dispatchEvent(
              new CustomEvent('cart:updated', {
                bubbles: true,
                detail: { cart: cartContent },
              })
            );

            // The drawer must not slide open a beat before the page leaves for
            // checkout, so the redirect decides openMiniCart. The refresh itself
            // still fires — the header count and any cart section update either
            // way, which matters if the redirect is blocked.
            const goingToCheckout = this.dataset.redirectToCheckout === 'true';

            document.documentElement.dispatchEvent(
              new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: {
                  cart: cartContent,
                  openMiniCart: !goingToCheckout,
                },
              })
            );

            if (goingToCheckout) {
              redirectingToCheckout = true;
              const checkoutUrl =
                window.themeVariables?.routes?.checkoutUrl ||
                window.routes?.checkout_url ||
                '/checkout';
              window.location.href = checkoutUrl;
              return;
            }

            form.dispatchEvent(
              new CustomEvent('cart-notification:show', {
                bubbles: true,
                cancelable: true,
                detail: { status: 'success', error: '' },
              })
            );
          })
          .catch(err => console.error(err))
          .finally(() => {
            this._isSubmittingBundle = false;
            if (!redirectingToCheckout) {
              this._setSubmitButtonLoading(submitBtn, false);
            }
          });
      }

    }
  );
}

